import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { adminAPI, uploadAPI, authAPI, gradesAPI, enrollmentsAPI } from '../services/api';
import AnalyticsCharts from './AnalyticsCharts';
import StudentQRCode from './StudentQRCode';
import DefaultAvatar from '../components/DefaultAvatar';
import { 
  FiUsers, 
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiLogOut,
  FiUser,
  FiGrid,
  FiList,
  FiPlus,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiMessageSquare,
  FiActivity,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiMenu,
  FiCamera,
  FiMoon,
  FiSun,
  FiBarChart2,
  FiKey,
  FiLock,
  FiBook,
  FiAward,
  FiSave,
  FiEye,
  FiEyeOff,
  FiClipboard,
  FiUserCheck,
  FiCopy,
} from 'react-icons/fi';
import { BsQrCode } from 'react-icons/bs';
import './AdminDashboard.css';

// Helper function to get the correct image URL (handles both local and Supabase URLs)
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If URL already starts with http/https, it's a full URL (Supabase)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Otherwise, it's a local path that needs the server prefix
  return `http://localhost:5000${imageUrl}`;
};

const AdminDashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [enrollmentsPagination, setEnrollmentsPagination] = useState({ page: 1, limit: 10 });
  const [dashboardData, setDashboardData] = useState(null);
  const [, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    yearLevel: '',
    section: '',
    enrollmentStatus: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  
  // Modal states
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordTeacher, setResetPasswordTeacher] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [modalError, setModalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [imagePreviewModal, setImagePreviewModal] = useState({ show: false, src: '', alt: '' });
  const [qrCodeStudent, setQrCodeStudent] = useState(null);

  // Grades states
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState(1);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');
  const [selectedYearLevel, setSelectedYearLevel] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedStudentForGrades, setSelectedStudentForGrades] = useState(null);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [studentGWA, setStudentGWA] = useState(null);
  const [currentSubjectIndex, setCurrentSubjectIndex] = useState(0);
  const pendingSubjectRef = useRef(null);
  const [gradeForm, setGradeForm] = useState({
    studentId: '',
    courseCode: '',
    subjectTitle: '',
    units: 3,
    grade: '',
    semester: 1,
    schoolYear: '',
    instructor: '',
    yearLevel: 1,
    period: 'prelim',
    section: '',
  });
  const [availableSections, setAvailableSections] = useState([]);
  const [sectionAutoFilled, setSectionAutoFilled] = useState(false); // Track if section was auto-filled from enrollment
  const [studentEnrolled, setStudentEnrolled] = useState(false); // Track if student is enrolled in the subject
  const [subjectForm, setSubjectForm] = useState({
    courseCode: '',
    subjectTitle: '',
    units: 3,
    semester: 1,
    yearLevel: 1,
    section: '',
    preRequisite: '',
    instructorFirstName: '',
    instructorLastName: '',
    scheduleDay: '',
    scheduleTimeStart: '',
    scheduleTimeEnd: '',
    room: '',
  });

  // Subjects tab filter state (independent from Grades tab)
  const [subjectsSemesterFilter, setSubjectsSemesterFilter] = useState(1);
  const [subjectsYearLevelFilter, setSubjectsYearLevelFilter] = useState(null);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  // Enrollments state
  const [enrollments, setEnrollments] = useState([]);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [, setEnrollmentMode] = useState('single'); // 'single' or 'bulk'
  const [selectedStudentsForEnrollment, setSelectedStudentsForEnrollment] = useState([]);
  const [selectedSubjectForEnrollment, setSelectedSubjectForEnrollment] = useState(null);
  const [enrollmentFilters, setEnrollmentFilters] = useState({
    semester: 1,
    yearLevel: null,
    schoolYear: '',
    courseCode: '',
    section: '',
  });
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [showEditEnrollmentModal, setShowEditEnrollmentModal] = useState(false);
  const [editEnrollmentForm, setEditEnrollmentForm] = useState({ classNumber: '' });

  // Form data
  const [studentForm, setStudentForm] = useState({
    studentId: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    email: '',
    contactNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    yearLevel: 4, // Default to 4th year only
    section: 'A',
    program: 'Bachelor of Science in Computer Science',
    enrollmentStatus: 'Enrolled',
    academicStatus: 'Regular',
    enrollmentDate: '',
    expectedGraduationDate: '',
    birthplace: '',
    fbAccount: '',
    fatherName: '',
    fatherFbAccount: '',
    fatherContactNumber: '',
    fatherAddress: '',
    motherMaidenName: '',
    motherFbAccount: '',
    motherContactNumber: '',
    motherAddress: '',
    permanentAddress: '',
    guardianName: '',
    guardianContact: '',
    profileImage: '',
  });

  const [teacherForm, setTeacherForm] = useState({
    employeeId: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    position: '',
    contactNumber: '',
    role: 'teacher',
    profileImage: '',
  });
  
  // Teacher profile image states
  const [teacherProfileImageFile, setTeacherProfileImageFile] = useState(null);
  const [teacherProfileImagePreview, setTeacherProfileImagePreview] = useState(null);
  const [showTeacherPassword, setShowTeacherPassword] = useState(false);
  const teacherFileInputRef = useRef(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'students') {
      loadStudents();
    } else if (activeTab === 'teachers') {
      loadTeachers();
    } else if (activeTab === 'logs') {
      loadActivityLogs();
    } else if (activeTab === 'grades') {
      loadGradesData();
    } else if (activeTab === 'subjects') {
      loadSubjectsData();
    }
  }, [activeTab, pagination.page, filters, searchTerm, logsPagination.page, subjectsSemesterFilter, subjectsYearLevelFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-reload enrollments when filters change
  useEffect(() => {
    if (activeTab === 'enrollments') {
      loadEnrollments();
      setEnrollmentsPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
    }
  }, [activeTab, enrollmentFilters.semester, enrollmentFilters.yearLevel, enrollmentFilters.schoolYear, enrollmentFilters.courseCode, enrollmentFilters.section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to the subject that was just graded after grades reload
  useEffect(() => {
    if (pendingSubjectRef.current && grades.length > 0) {
      const uniqueSubjects = getUniqueSubjects();
      const idx = uniqueSubjects.findIndex(s => s.courseCode === pendingSubjectRef.current);
      if (idx !== -1) {
        setCurrentSubjectIndex(idx);
      }
      pendingSubjectRef.current = null;
    }
  }, [grades]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard();
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const response = await adminAPI.getLogs({ 
        page: logsPagination.page, 
        limit: logsPagination.limit 
      });
      if (response.data.success) {
        setActivityLogs(response.data.data.logs);
        setLogsPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total,
          totalPages: response.data.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [logsPagination.page, logsPagination.limit]);

  // ==================== GRADES FUNCTIONS ====================
  const loadGradesData = useCallback(async () => {
    try {
      setGradesLoading(true);
      // Load students for the list
      const studentsResponse = await adminAPI.getStudents({ limit: 9999 });
      if (studentsResponse.data.success) {
        setStudents(studentsResponse.data.data.students);
      }
      // Load school years
      const yearsResponse = await gradesAPI.getSchoolYears();
      if (yearsResponse.data.success) {
        setSchoolYears(yearsResponse.data.data);
        if (!selectedSchoolYear && yearsResponse.data.data.length > 0) {
          setSelectedSchoolYear(yearsResponse.data.data[0]);
        }
      }
      // Load ALL subjects (no filtering) - needed for Add Grade modal dropdown
      // The modal does its own filtering based on the selected year level and semester
      const subjectsResponse = await gradesAPI.getSubjects();
      if (subjectsResponse.data.success) {
        setSubjects(subjectsResponse.data.data);
      }
    } catch (error) {
      console.error('Failed to load grades data:', error);
    } finally {
      setGradesLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== SUBJECTS FUNCTIONS ====================
  const loadSubjectsData = useCallback(async () => {
    try {
      setSubjectsLoading(true);
      // Load subjects using subjects tab's own filters
      const semesterParam = subjectsSemesterFilter || null;
      const subjectsResponse = await gradesAPI.getSubjects(semesterParam);
      if (subjectsResponse.data.success) {
        let subjectsData = subjectsResponse.data.data;
        // Filter subjects by year level if selected (using subjects tab filter)
        if (subjectsYearLevelFilter !== null) {
          subjectsData = subjectsData.filter(subject => subject.yearLevel === subjectsYearLevelFilter);
        }
        setSubjects(subjectsData);
      }
    } catch (error) {
      console.error('Failed to load subjects data:', error);
    } finally {
      setSubjectsLoading(false);
    }
  }, [subjectsSemesterFilter, subjectsYearLevelFilter]);

  const handleSemesterChangeForSubjects = async (semester) => {
    const newSemester = semester ? parseInt(semester) : null;
    setSubjectsSemesterFilter(newSemester);
    
    try {
      setSubjectsLoading(true);
      const subjectsResponse = await gradesAPI.getSubjects(newSemester);
      if (subjectsResponse.data.success) {
        let subjectsData = subjectsResponse.data.data;
        // Filter subjects by year level if selected (using subjects tab filter)
        if (subjectsYearLevelFilter !== null) {
          subjectsData = subjectsData.filter(subject => subject.yearLevel === subjectsYearLevelFilter);
        }
        setSubjects(subjectsData);
      }
    } catch (error) {
      console.error('Failed to load subjects:', error);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const handleYearLevelChangeForSubjects = async (yearLevel) => {
    const newYearLevel = yearLevel ? parseInt(yearLevel) : null;
    setSubjectsYearLevelFilter(newYearLevel);
    
    // Reset semester if Summer (3) was selected but year level is no longer 4
    let currentSemester = subjectsSemesterFilter;
    if (newYearLevel !== 4 && subjectsSemesterFilter === 3) {
      currentSemester = 1;
      setSubjectsSemesterFilter(1);
    }
    
    try {
      setSubjectsLoading(true);
      const subjectsResponse = await gradesAPI.getSubjects(currentSemester);
      if (subjectsResponse.data.success) {
        let subjectsData = subjectsResponse.data.data;
        if (newYearLevel !== null) {
          subjectsData = subjectsData.filter(subject => subject.yearLevel === newYearLevel);
        }
        setSubjects(subjectsData);
      }
    } catch (error) {
      console.error('Failed to load subjects:', error);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const loadStudentGrades = async (studentId, semester = selectedSemester, schoolYear = selectedSchoolYear, yearLevel = selectedYearLevel) => {
    try {
      setGradesLoading(true);
      const response = await gradesAPI.getStudentGrades(studentId, semester, schoolYear, yearLevel);
      if (response.data.success) {
        let gradesData = response.data.data;
        // Filter by year level if selected
        if (yearLevel !== null) {
          gradesData = gradesData.filter(grade => grade.yearLevel === yearLevel);
        }
        // Filter by period if selected
        if (selectedPeriod !== null) {
          gradesData = gradesData.filter(grade => grade.period === selectedPeriod);
        }
        setGrades(gradesData);
        setStudentGWA(response.data.gwa);
      }
    } catch (error) {
      console.error('Failed to load student grades:', error);
    } finally {
      setGradesLoading(false);
    }
  };

  const handleSelectStudentForGrades = async (student) => {
    setSelectedStudentForGrades(student);
    setCurrentSubjectIndex(0); // Reset to first subject
    
    // Ensure schoolYear matches the current yearLevel filter
    let syToUse = selectedSchoolYear;
    if (selectedYearLevel !== null) {
      syToUse = getSchoolYearFromYearLevel(selectedYearLevel);
      setSelectedSchoolYear(syToUse);
    }

    await loadStudentGrades(student.studentId, selectedSemester, syToUse);
  };

  const handleSemesterChange = async (semester) => {
    setSelectedSemester(parseInt(semester));
    setCurrentSubjectIndex(0); // Reset to first subject
    setSelectedSubject(null); // Reset subject filter when semester changes
    if (selectedStudentForGrades) {
      // Reload grades for current student with new semester
      setGradesLoading(true);
      try {
        const response = await gradesAPI.getStudentGrades(
          selectedStudentForGrades.studentId, 
          parseInt(semester), 
          selectedSchoolYear,
          selectedYearLevel
        );
        if (response.data.success) {
          let gradesData = response.data.data;
          // Filter by year level if selected
          if (selectedYearLevel !== null) {
            gradesData = gradesData.filter(grade => grade.yearLevel === selectedYearLevel);
          }
          // Filter by period if selected
          if (selectedPeriod !== null) {
            gradesData = gradesData.filter(grade => grade.period === selectedPeriod);
          }
          setGrades(gradesData);
          setStudentGWA(response.data.gwa);
        }
      } catch (error) {
        console.error('Failed to load grades:', error);
      } finally {
        setGradesLoading(false);
      }
    }
  };

  const handleSchoolYearChange = async (schoolYear) => {
    setSelectedSchoolYear(schoolYear);
    setCurrentSubjectIndex(0); // Reset to first subject
    if (selectedStudentForGrades) {
      setGradesLoading(true);
      try {
        const response = await gradesAPI.getStudentGrades(
          selectedStudentForGrades.studentId, 
          selectedSemester, 
          schoolYear,
          selectedYearLevel
        );
        if (response.data.success) {
          let gradesData = response.data.data;
          // Filter by year level if selected
          if (selectedYearLevel !== null) {
            gradesData = gradesData.filter(grade => grade.yearLevel === selectedYearLevel);
          }
          // Filter by period if selected
          if (selectedPeriod !== null) {
            gradesData = gradesData.filter(grade => grade.period === selectedPeriod);
          }
          setGrades(gradesData);
          setStudentGWA(response.data.gwa);
        }
      } catch (error) {
        console.error('Failed to load grades:', error);
      } finally {
        setGradesLoading(false);
      }
    }
  };

  const handleYearLevelChange = async (yearLevel) => {
    const newYearLevel = yearLevel ? parseInt(yearLevel) : null;
    setSelectedYearLevel(newYearLevel);
    setCurrentSubjectIndex(0); // Reset to first subject
    setSelectedSubject(null); // Reset subject filter when year level changes
    
    // Reset semester if Summer (3) was selected but year level is no longer 4
    if (newYearLevel !== 4 && selectedSemester === 3) {
      setSelectedSemester(1);
    }
    
    // Auto-select school year based on year level
    let newSchoolYear = selectedSchoolYear;
    if (newYearLevel !== null) {
      newSchoolYear = getSchoolYearFromYearLevel(newYearLevel);
      setSelectedSchoolYear(newSchoolYear);
    }
    
    if (selectedStudentForGrades) {
      setGradesLoading(true);
      try {
        const response = await gradesAPI.getStudentGrades(
          selectedStudentForGrades.studentId, 
          selectedSemester, 
          newSchoolYear,
          newYearLevel
        );
        if (response.data.success) {
          let gradesData = response.data.data;
          // Filter by year level if selected
          if (newYearLevel !== null) {
            gradesData = gradesData.filter(grade => grade.yearLevel === newYearLevel);
          }
          // Filter by period if selected
          if (selectedPeriod !== null) {
            gradesData = gradesData.filter(grade => grade.period === selectedPeriod);
          }
          setGrades(gradesData);
          setStudentGWA(response.data.gwa);
        }
      } catch (error) {
        console.error('Failed to load grades:', error);
      } finally {
        setGradesLoading(false);
      }
    }
    
  };

  const handlePeriodChange = async (period) => {
    const newPeriod = period || null;
    setSelectedPeriod(newPeriod);
    
    if (selectedStudentForGrades) {
      setGradesLoading(true);
      try {
        const response = await gradesAPI.getStudentGrades(
          selectedStudentForGrades.studentId, 
          selectedSemester, 
          selectedSchoolYear,
          selectedYearLevel
        );
        if (response.data.success) {
          let gradesData = response.data.data;
          // Filter by year level if selected
          if (selectedYearLevel !== null) {
            gradesData = gradesData.filter(grade => grade.yearLevel === selectedYearLevel);
          }
          // Filter by period if selected
          if (newPeriod !== null) {
            gradesData = gradesData.filter(grade => grade.period === newPeriod);
          }
          setGrades(gradesData);
          setStudentGWA(response.data.gwa);
        }
      } catch (error) {
        console.error('Failed to load grades:', error);
      } finally {
        setGradesLoading(false);
      }
    }
  };

  const handleSubjectChange = (courseCode) => {
    setSelectedSubject(courseCode || null);
  };

  // ==================== ENROLLMENT FUNCTIONS ====================
  
  // Load all subjects for enrollment management (unfiltered by Grades tab)
  const loadEnrollmentsData = async () => {
    try {
      // Load ALL subjects (not filtered by selectedYearLevel from Grades tab)
      const subjectsResponse = await gradesAPI.getSubjects(null);
      if (subjectsResponse.data.success) {
        setSubjects(subjectsResponse.data.data);
      }
      // Load students for enrollment
      const studentsResponse = await adminAPI.getStudents({ limit: 500 });
      if (studentsResponse.data.success) {
        setStudents(studentsResponse.data.data.students);
      }
      // Then load enrollments
      await loadEnrollments();
    } catch (error) {
      console.error('Failed to load enrollment data:', error);
    }
  };
  
  const loadEnrollments = useCallback(async () => {
    try {
      const params = {};
      if (enrollmentFilters.semester) params.semester = enrollmentFilters.semester;
      if (enrollmentFilters.yearLevel) params.yearLevel = enrollmentFilters.yearLevel;
      if (enrollmentFilters.schoolYear) params.schoolYear = enrollmentFilters.schoolYear;
      if (enrollmentFilters.courseCode) params.courseCode = enrollmentFilters.courseCode;
      if (enrollmentFilters.section) params.section = enrollmentFilters.section;
      
      const response = await enrollmentsAPI.getEnrollments(params);
      if (response.data.success) {
        setEnrollments(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load enrollments:', error);
    }
  }, [enrollmentFilters.semester, enrollmentFilters.yearLevel, enrollmentFilters.schoolYear, enrollmentFilters.courseCode, enrollmentFilters.section]);

  const handleEnrollStudent = async (subject) => {
    setSelectedSubjectForEnrollment(subject);
    setEnrollmentMode('bulk');
    setSelectedStudentsForEnrollment([]);
    setShowEnrollmentModal(true);
  };

  const handleToggleStudentForEnrollment = (student) => {
    setSelectedStudentsForEnrollment(prev => {
      const exists = prev.find(s => s.studentId === student.studentId);
      if (exists) {
        return prev.filter(s => s.studentId !== student.studentId);
      }
      return [...prev, student];
    });
  };

  const handleSelectAllStudentsForEnrollment = () => {
    if (!selectedSubjectForEnrollment) return;
    
    // Get eligible students (not already enrolled in any section of this subject)
    const eligibleStudents = students.filter(student => {
      const isAlreadyEnrolled = enrollments.some(e => 
        e.studentId === student.studentId &&
        e.courseCode === selectedSubjectForEnrollment.courseCode &&
        e.semester === selectedSubjectForEnrollment.semester &&
        e.yearLevel === selectedSubjectForEnrollment.yearLevel
      );
      return !isAlreadyEnrolled;
    });
    
    if (selectedStudentsForEnrollment.length === eligibleStudents.length) {
      setSelectedStudentsForEnrollment([]);
    } else {
      setSelectedStudentsForEnrollment([...eligibleStudents]);
    }
  };

  const handleSubmitEnrollments = async () => {
    if (!selectedSubjectForEnrollment || selectedStudentsForEnrollment.length === 0) {
      setModalError('Please select at least one student');
      return;
    }

    try {
      const enrollmentsToCreate = selectedStudentsForEnrollment.map(student => ({
        studentId: student.studentId,
        courseCode: selectedSubjectForEnrollment.courseCode,
        subjectTitle: selectedSubjectForEnrollment.subjectTitle,
        section: selectedSubjectForEnrollment.section,
        semester: selectedSubjectForEnrollment.semester,
        yearLevel: selectedSubjectForEnrollment.yearLevel,
        schoolYear: getSchoolYearFromYearLevel(selectedSubjectForEnrollment.yearLevel),
        instructorFirstName: selectedSubjectForEnrollment.instructorFirstName || '',
        instructorLastName: selectedSubjectForEnrollment.instructorLastName || '',
      }));

      const response = await enrollmentsAPI.bulkEnroll(enrollmentsToCreate);
      
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowEnrollmentModal(false);
        setSelectedStudentsForEnrollment([]);
        setSelectedSubjectForEnrollment(null);
        loadEnrollments();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error enrolling students:', error);
      setModalError(error.response?.data?.message || 'Failed to enroll students');
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to remove this enrollment?')) return;
    
    try {
      await enrollmentsAPI.deleteEnrollment(enrollmentId);
      setSuccessMessage('Enrollment removed successfully!');
      loadEnrollments();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to delete enrollment:', error);
    }
  };

  const handleEditEnrollment = (enrollment) => {
    setEditingEnrollment(enrollment);
    setEditEnrollmentForm({
      classNumber: enrollment.classNumber || ''
    });
    setShowEditEnrollmentModal(true);
  };

  const handleSaveEnrollment = async (e) => {
    e.preventDefault();
    if (!editingEnrollment) return;

    try {
      await enrollmentsAPI.updateEnrollment(editingEnrollment.id, {
        section: editingEnrollment.section,
        classNumber: parseInt(editEnrollmentForm.classNumber) || null,
        instructorFirstName: editingEnrollment.instructorFirstName,
        instructorLastName: editingEnrollment.instructorLastName,
      });
      
      setSuccessMessage('Class number updated successfully!');
      setShowEditEnrollmentModal(false);
      setEditingEnrollment(null);
      loadEnrollments();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating enrollment:', error);
      setModalError(error.response?.data?.message || 'Failed to update enrollment');
    }
  };

  const handleEnrollmentFilterChange = (name, value) => {
    setEnrollmentFilters(prev => {
      const updated = { ...prev, [name]: value };
      // Reset semester if year level changed and Summer (3) was selected but year is no longer 4
      if (name === 'yearLevel' && value !== 4 && prev.semester === 3) {
        updated.semester = null;
      }
      return updated;
    });
  };

  // Get unique subjects with sections for enrollment management
  const getSubjectsWithSections = () => {
    return subjects.filter(s => 
      (!enrollmentFilters.yearLevel || s.yearLevel === parseInt(enrollmentFilters.yearLevel)) &&
      (!enrollmentFilters.semester || s.semester === parseInt(enrollmentFilters.semester))
    );
  };

  // Check student enrollment for grade auto-fill
  const checkStudentEnrollment = async (studentId, courseCode, semester, yearLevel, schoolYear) => {
    try {
      const response = await enrollmentsAPI.checkEnrollment({
        studentId,
        courseCode,
        semester,
        yearLevel,
        schoolYear
      });
      return response.data;
    } catch (error) {
      console.error('Error checking enrollment:', error);
      return { enrolled: false, data: null };
    }
  };

  // Helper function to calculate school year based on year level
  const getSchoolYearFromYearLevel = (yearLevel) => {
    const yearLevelNum = parseInt(yearLevel);
    const baseYear = 2022;
    const startYear = baseYear + (yearLevelNum - 1);
    return `${startYear}-${startYear + 1}`;
  };

  // Helper function to check if a subject is fully graded (all periods complete)
  const isSubjectFullyGraded = (courseCode, semester, yearLevel, schoolYear) => {
    // For Summer/OJT (semester 3), only need OJT grade
    if (semester === 3) {
      const ojtGrade = grades.find(g => 
        g.courseCode === courseCode && 
        g.semester === semester && 
        g.yearLevel === yearLevel &&
        g.schoolYear === schoolYear &&
        g.period === 'ojt' &&
        g.grade !== null
      );
      return !!ojtGrade;
    }
    
    // For regular semesters, need all 4 periods (prelim, midterm, prefinal, final)
    const requiredPeriods = ['prelim', 'midterm', 'prefinal', 'final'];
    return requiredPeriods.every(period => {
      const gradeExists = grades.find(g => 
        g.courseCode === courseCode && 
        g.semester === semester && 
        g.yearLevel === yearLevel &&
        g.schoolYear === schoolYear &&
        g.period === period &&
        g.grade !== null
      );
      return gradeExists;
    });
  };

  // Helper function to check if a subject already has a grade for a specific period
  const isSubjectGradedForPeriod = (courseCode, semester, yearLevel, schoolYear, period) => {
    const existingGrade = grades.find(g => 
      g.courseCode === courseCode && 
      g.semester === semester && 
      g.yearLevel === yearLevel &&
      g.schoolYear === schoolYear &&
      g.period === period &&
      g.grade !== null
    );
    return !!existingGrade;
  };

  // Helper function to format time from 24-hour to 12-hour format with AM/PM
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to calculate final grade for a subject
  const calculateFinalGrade = (courseCode, semester, yearLevel, schoolYear) => {
    // For Summer/OJT subjects, there's only one grade (no periods)
    if (semester === 3) {
      const grade = grades.find(g => 
        g.courseCode === courseCode && 
        g.semester === semester && 
        g.yearLevel === yearLevel &&
        g.schoolYear === schoolYear &&
        g.grade !== null
      );
      if (grade && !isNaN(grade.grade) && grade.grade !== 'DRP') {
        return parseFloat(grade.grade).toFixed(2);
      }
      return null;
    }
    
    // For regular semesters, calculate average of all 4 periods
    const periods = ['prelim', 'midterm', 'prefinal', 'final'];
    const periodGrades = [];
    
    periods.forEach(period => {
      const grade = grades.find(g => 
        g.courseCode === courseCode && 
        g.semester === semester && 
        g.yearLevel === yearLevel &&
        g.schoolYear === schoolYear &&
        g.period === period &&
        g.grade !== null
      );
      if (grade && !isNaN(grade.grade) && grade.grade !== 'DRP') {
        periodGrades.push(parseFloat(grade.grade));
      }
    });
    
    // Only calculate final grade if all 4 periods are graded
    if (periodGrades.length === 4) {
      const sum = periodGrades.reduce((acc, grade) => acc + grade, 0);
      const avg = sum / 4;
      return (+(Math.round(parseFloat(avg + 'e+2')) + 'e-2')).toFixed(2);
    }
    
    return null;
  };

  // Helper function to get unique subjects from grades
  const getUniqueSubjects = () => {
    const subjectsMap = new Map();
    
    grades.forEach(grade => {
      const key = `${grade.courseCode}-${grade.semester}-${grade.yearLevel}-${grade.schoolYear}`;
      if (!subjectsMap.has(key)) {
        subjectsMap.set(key, {
          courseCode: grade.courseCode,
          subjectTitle: grade.subjectTitle,
          units: grade.units,
          semester: grade.semester,
          yearLevel: grade.yearLevel,
          schoolYear: grade.schoolYear,
          instructor: grade.instructor
        });
      }
    });
    
    let uniqueSubjects = Array.from(subjectsMap.values());
    
    // Filter by selected subject if one is selected
    if (selectedSubject) {
      uniqueSubjects = uniqueSubjects.filter(s => s.courseCode === selectedSubject);
    }
    
    return uniqueSubjects;
  };

  const handleAddGrade = () => {
    if (!selectedStudentForGrades) {
      setModalError('Please select a student first');
      return;
    }
    setEditingGrade(null);
    setAvailableSections([]);
    setSectionAutoFilled(false);
    setStudentEnrolled(false);
    const defaultYearLevel = selectedYearLevel !== null ? selectedYearLevel : 1;
    setGradeForm({
      studentId: selectedStudentForGrades.studentId,
      courseCode: '',
      subjectTitle: '',
      units: 3,
      grade: '',
      semester: selectedSemester,
      schoolYear: getSchoolYearFromYearLevel(defaultYearLevel),
      instructor: '',
      yearLevel: defaultYearLevel,
      period: selectedSemester === 3 ? 'ojt' : 'prelim',
      section: '',
    });
    setShowGradeModal(true);
  };

  const handleEditGrade = (grade) => {
    setEditingGrade(grade);
    setSectionAutoFilled(false);
    setStudentEnrolled(true); // When editing, student was enrolled
    
    // Find available sections for this course code
    const sectionsForCourse = subjects.filter(s => 
      s.courseCode === grade.courseCode && 
      s.yearLevel === (grade.yearLevel || 1) && 
      s.semester === grade.semester
    );
    setAvailableSections(sectionsForCourse);
    
    setGradeForm({
      studentId: grade.studentId,
      courseCode: grade.courseCode,
      subjectTitle: grade.subjectTitle,
      units: grade.units,
      grade: grade.grade !== null ? grade.grade : '',
      semester: grade.semester,
      schoolYear: grade.schoolYear,
      instructor: grade.instructor || '',
      yearLevel: grade.yearLevel || 1,
      period: grade.period || 'prelim',
      section: grade.section || '',
    });
    setShowGradeModal(true);
  };

  const handleGradeFormChange = async (e) => {
    const { name, value } = e.target;
    
    // If yearLevel changes, auto-calculate schoolYear and clear courseCode
    if (name === 'yearLevel') {
      const newYearLevel = parseInt(value);
      setGradeForm(prev => ({ 
        ...prev, 
        yearLevel: newYearLevel,
        schoolYear: getSchoolYearFromYearLevel(value),
        courseCode: '',
        subjectTitle: '',
        instructor: '',
        section: '',
        // Reset semester if Summer (3) was selected but year level is no longer 4
        semester: (newYearLevel !== 4 && prev.semester === 3) ? 1 : prev.semester,
        period: (newYearLevel !== 4 && prev.semester === 3) ? 'prelim' : prev.period
      }));
      setAvailableSections([]);
      setSectionAutoFilled(false);
    }
    // If semester changes, clear the courseCode to avoid mismatch
    // For Summer (OJT), set period to 'ojt' since there's no grading period
    else if (name === 'semester') {
      const semesterVal = parseInt(value);
      setGradeForm(prev => ({ 
        ...prev, 
        semester: semesterVal,
        courseCode: '',
        subjectTitle: '',
        instructor: '',
        period: semesterVal === 3 ? 'ojt' : 'prelim',
        section: ''
      }));
      setAvailableSections([]);
      setSectionAutoFilled(false);
      setStudentEnrolled(false);
    } 
    // When course code is selected, check enrollment first then show available sections
    else if (name === 'courseCode' && value) {
      // Find all sections for this course code
      const sectionsForCourse = subjects.filter(s => 
        s.courseCode === value && 
        s.yearLevel === gradeForm.yearLevel && 
        s.semester === gradeForm.semester
      );
      
      setAvailableSections(sectionsForCourse);
      
      // Check if student is enrolled in this subject
      const schoolYear = getSchoolYearFromYearLevel(gradeForm.yearLevel);
      const enrollmentCheck = await checkStudentEnrollment(
        gradeForm.studentId,
        value,
        gradeForm.semester,
        gradeForm.yearLevel,
        schoolYear
      );
      
      if (enrollmentCheck.enrolled && enrollmentCheck.data) {
        // Student is enrolled - auto-fill section from enrollment (ONLY allowed section)
        const enrollment = enrollmentCheck.data;
        const subject = sectionsForCourse.find(s => s.section === enrollment.section);
        const instructorName = enrollment.instructorFirstName && enrollment.instructorLastName 
          ? `${enrollment.instructorFirstName} ${enrollment.instructorLastName}` 
          : '';
        setGradeForm(prev => ({
          ...prev,
          courseCode: value,
          subjectTitle: enrollment.subjectTitle,
          units: subject ? subject.units : prev.units,
          instructor: instructorName,
          section: enrollment.section
        }));
        setSectionAutoFilled(true);
        setStudentEnrolled(true);
        // Only show the enrolled section as available
        setAvailableSections(sectionsForCourse.filter(s => s.section === enrollment.section));
      } else {
        // Student is NOT enrolled - they cannot be graded in this subject
        const firstSubject = sectionsForCourse[0];
        setGradeForm(prev => ({
          ...prev,
          courseCode: value,
          subjectTitle: firstSubject ? firstSubject.subjectTitle : '',
          units: firstSubject ? firstSubject.units : prev.units,
          instructor: '',
          section: ''
        }));
        setSectionAutoFilled(false);
        setStudentEnrolled(false);
      }
    }
    // When section is selected manually (only if enrolled)
    else if (name === 'section' && value) {
      setSectionAutoFilled(false);
      const subject = subjects.find(s => 
        s.courseCode === gradeForm.courseCode && 
        s.section === value &&
        s.yearLevel === gradeForm.yearLevel && 
        s.semester === gradeForm.semester
      );
      if (subject) {
        const instructorName = subject.instructorFirstName && subject.instructorLastName 
          ? `${subject.instructorFirstName} ${subject.instructorLastName}` 
          : '';
        setGradeForm(prev => ({
          ...prev,
          section: value,
          instructor: instructorName,
          units: subject.units,
        }));
      } else {
        setGradeForm(prev => ({ ...prev, section: value }));
      }
    }
    else {
      setGradeForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmitGrade = async (e) => {
    e.preventDefault();
    setModalError('');
    
    try {
      const gradeData = {
        ...gradeForm,
        grade: gradeForm.grade !== '' ? parseFloat(gradeForm.grade) : null,
        units: parseFloat(gradeForm.units),
      };

      const gradedCourseCode = gradeForm.courseCode;

      if (editingGrade) {
        await gradesAPI.updateGrade(editingGrade.id, gradeData);
        setSuccessMessage('Grade updated successfully!');
      } else {
        await gradesAPI.createGrade(gradeData);
        setSuccessMessage('Grade added successfully!');
      }
      
      setShowGradeModal(false);
      pendingSubjectRef.current = gradedCourseCode;
      await loadStudentGrades(selectedStudentForGrades.studentId);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setModalError(error.response?.data?.message || 'Failed to save grade');
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('Are you sure you want to delete this grade?')) return;
    
    try {
      await gradesAPI.deleteGrade(gradeId);
      setSuccessMessage('Grade deleted successfully!');
      await loadStudentGrades(selectedStudentForGrades.studentId);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to delete grade:', error);
    }
  };

  const handleAddSubject = () => {
    setEditingSubject(null);
    setSubjectForm({
      courseCode: '',
      subjectTitle: '',
      units: 3,
      semester: subjectsSemesterFilter || 1,
      yearLevel: subjectsYearLevelFilter || 1,
      section: '',
      preRequisite: '',
      instructorFirstName: '',
      instructorLastName: '',
      scheduleDay: '',
      scheduleTimeStart: '',
      scheduleTimeEnd: '',
      room: '',
    });
    setShowSubjectModal(true);
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectForm({
      courseCode: subject.courseCode,
      subjectTitle: subject.subjectTitle,
      units: subject.units,
      semester: subject.semester,
      yearLevel: subject.yearLevel || 1,
      section: subject.section || '',
      preRequisite: subject.preRequisite || '',
      instructorFirstName: subject.instructorFirstName || '',
      instructorLastName: subject.instructorLastName || '',
      scheduleDay: subject.scheduleDay || '',
      scheduleTimeStart: subject.scheduleTimeStart || '',
      scheduleTimeEnd: subject.scheduleTimeEnd || '',
      room: subject.room || '',
    });
    setShowSubjectModal(true);
  };

  // Duplicate subject - copies all data but clears section for new entry
  const handleDuplicateSubject = (subject) => {
    setEditingSubject(null); // This is a new subject, not editing
    setSubjectForm({
      courseCode: subject.courseCode,
      subjectTitle: subject.subjectTitle,
      units: subject.units,
      semester: subject.semester,
      yearLevel: subject.yearLevel || 1,
      section: '', // Clear section so admin enters new section
      preRequisite: subject.preRequisite || '',
      instructorFirstName: subject.instructorFirstName || '',
      instructorLastName: subject.instructorLastName || '',
      scheduleDay: subject.scheduleDay || '',
      scheduleTimeStart: subject.scheduleTimeStart || '',
      scheduleTimeEnd: subject.scheduleTimeEnd || '',
      room: subject.room || '',
    });
    setShowSubjectModal(true);
  };

  const handleSubjectFormChange = (e) => {
    const { name, value } = e.target;
    setSubjectForm(prev => {
      const newForm = { ...prev, [name]: value };
      // If year level changes away from 4th year and semester is Summer (3), reset to 1st semester
      if (name === 'yearLevel' && parseInt(value) !== 4 && prev.semester === 3) {
        newForm.semester = 1;
      }
      return newForm;
    });
  };

  const handleSubmitSubject = async (e) => {
    e.preventDefault();
    setModalError('');
    
    try {
      // Check for duplicate section across ALL subjects in the same semester & year level
      if (subjectForm.section) {
        const allSubjectsResponse = await gradesAPI.getSubjects(subjectForm.semester);
        if (allSubjectsResponse.data.success) {
          const allSubjects = allSubjectsResponse.data.data;
          const duplicate = allSubjects.find(s =>
            s.section.toLowerCase() === subjectForm.section.toLowerCase() &&
            s.yearLevel === parseInt(subjectForm.yearLevel) &&
            parseInt(s.semester) === parseInt(subjectForm.semester) &&
            (!editingSubject || s.id !== editingSubject.id) // Exclude current subject when editing
          );
          if (duplicate) {
            setModalError(`Section "${subjectForm.section}" is already assigned to "${duplicate.courseCode} - ${duplicate.subjectTitle}" in Year ${subjectForm.yearLevel}, Semester ${subjectForm.semester}.`);
            return;
          }
        }
      }

      if (editingSubject) {
        await gradesAPI.updateSubject(editingSubject.id, subjectForm);
        setSuccessMessage('Subject updated successfully!');
      } else {
        await gradesAPI.createSubject(subjectForm);
        setSuccessMessage('Subject added successfully!');
      }
      
      setShowSubjectModal(false);
      // Reload subjects with subjects tab's own filters applied
      const subjectsResponse = await gradesAPI.getSubjects(subjectsSemesterFilter);
      if (subjectsResponse.data.success) {
        let subjectsData = subjectsResponse.data.data;
        // Filter by year level if selected (using subjects tab filter)
        if (subjectsYearLevelFilter !== null) {
          subjectsData = subjectsData.filter(subject => subject.yearLevel === subjectsYearLevelFilter);
        }
        setSubjects(subjectsData);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setModalError(error.response?.data?.message || 'Failed to save subject');
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;
    
    try {
      await gradesAPI.deleteSubject(subjectId);
      setSuccessMessage('Subject deleted successfully!');
      // Reload subjects with subjects tab's own filters applied
      const subjectsResponse = await gradesAPI.getSubjects(subjectsSemesterFilter);
      if (subjectsResponse.data.success) {
        let subjectsData = subjectsResponse.data.data;
        // Filter by year level if selected (using subjects tab filter)
        if (subjectsYearLevelFilter !== null) {
          subjectsData = subjectsData.filter(subject => subject.yearLevel === subjectsYearLevelFilter);
        }
        setSubjects(subjectsData);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to delete subject:', error);
    }
  };

  const getGradeColor = (grade) => {
    if (grade === null || grade === undefined) return 'var(--gray-400)';
    if (grade >= 1.0 && grade <= 1.5) return '#22c55e'; // Excellent - green
    if (grade > 1.5 && grade <= 2.0) return '#84cc16'; // Very good - lime
    if (grade > 2.0 && grade <= 2.5) return '#eab308'; // Good - yellow
    if (grade > 2.5 && grade <= 3.0) return '#f97316'; // Satisfactory - orange
    if (grade === 4.0) return '#ef4444'; // Conditional - red
    if (grade === 5.0) return '#dc2626'; // Failed - dark red
    return 'var(--gray-400)';
  };

  // ==================== END GRADES FUNCTIONS ====================

  const loadStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters,
      };
      const response = await adminAPI.getStudents(params);
      if (response.data.success) {
        setStudents(response.data.data.students);
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination,
        }));
      }
    } catch (error) {
      console.error('Failed to load students:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, filters]);

  const loadTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTeachers({ search: searchTerm });
      if (response.data.success) {
        setTeachers(response.data.data.teachers);
      }
    } catch (error) {
      console.error('Failed to load teachers:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const handleCreateStudent = () => {
    setEditingStudent(null);
    setStudentForm({
      studentId: '',
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      email: '',
      contactNumber: '',
      dateOfBirth: '',
      gender: '',
      yearLevel: 4, // Default to 4th year only
      section: 'A',
      program: 'Bachelor of Science in Computer Science',
      enrollmentStatus: 'Enrolled',
      academicStatus: 'Regular',
      enrollmentDate: new Date().toISOString().split('T')[0],
      expectedGraduationDate: '',
      birthplace: '',
      fbAccount: '',
      fatherName: '',
      fatherFbAccount: '',
      fatherContactNumber: '',
      fatherOccupation: '',
      fatherAddress: '',
      motherMaidenName: '',
      motherFbAccount: '',
      motherContactNumber: '',
      motherOccupation: '',
      motherAddress: '',
      guardianName: '',
      guardianContact: '',
      guardianAddress: '',
      profileImage: '',
      studentType: '',
      learnerReferenceNumber: '',
      civilStatus: '',
      cityResidenceType: '',
      masbateCityAddress: '',
      numberOfSiblings: '',
      birthOrder: '',
      studySupportPerson: '',
      studySupportRelationship: '',
      studySupportContact: '',
      parentType: '',
      monthlyFamilyIncome: '',
      tertiarySchool: '',
      tertiaryAddress: '',
      tertiaryDateGraduated: '',
      tertiaryCourse: '',
      seniorHighSchool: '',
      seniorHighAddress: '',
      seniorHighDateGraduated: '',
      seniorHighCourse: '',
      juniorHighSchool: '',
      juniorHighAddress: '',
      juniorHighDateGraduated: '',
      elementarySchool: '',
      elementaryAddress: '',
      elementaryDateGraduated: '',
    });
    setProfileImageFile(null);
    setProfileImagePreview(null);
    setModalError('');
    setShowStudentModal(true);
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setStudentForm({
      studentId: student.studentId,
      firstName: student.firstName,
      middleName: student.middleName || '',
      lastName: student.lastName,
      suffix: student.suffix || '',
      email: student.email,
      contactNumber: student.contactNumber || '',
      dateOfBirth: student.dateOfBirth ? (() => { const d = student.dateOfBirth.split('T')[0].split('-'); return d[1] + '/' + d[2] + '/' + d[0]; })() : '',
      gender: student.gender || '',
      yearLevel: student.yearLevel,
      section: student.section,
      program: student.program,
      enrollmentStatus: student.enrollmentStatus,
      academicStatus: student.academicStatus,
      enrollmentDate: student.enrollmentDate ? student.enrollmentDate.split('T')[0] : '',
      expectedGraduationDate: student.expectedGraduationDate ? student.expectedGraduationDate.split('T')[0] : '',
      birthplace: student.birthplace || '',
      fbAccount: student.fbAccount || '',
      fatherName: student.fatherName || '',
      fatherFbAccount: student.fatherFbAccount || '',
      fatherContactNumber: student.fatherContactNumber || '',
      fatherOccupation: student.fatherOccupation || '',
      fatherAddress: student.fatherAddress || '',
      motherMaidenName: student.motherMaidenName || '',
      motherFbAccount: student.motherFbAccount || '',
      motherContactNumber: student.motherContactNumber || '',
      motherOccupation: student.motherOccupation || '',
      motherAddress: student.motherAddress || '',
      guardianName: student.guardianName || '',
      guardianContact: student.guardianContact || '',
      guardianAddress: student.guardianAddress || '',
      profileImage: student.profileImage || '',
      studentType: student.studentType || '',
      learnerReferenceNumber: student.learnerReferenceNumber || '',
      civilStatus: student.civilStatus || '',
      cityResidenceType: student.cityResidenceType || '',
      masbateCityAddress: student.masbateCityAddress || '',
      numberOfSiblings: student.numberOfSiblings || '',
      birthOrder: student.birthOrder || '',
      studySupportPerson: student.studySupportPerson || '',
      studySupportRelationship: student.studySupportRelationship || '',
      studySupportContact: student.studySupportContact || '',
      parentType: student.parentType || '',
      monthlyFamilyIncome: student.monthlyFamilyIncome || '',
      tertiarySchool: student.tertiarySchool || '',
      tertiaryAddress: student.tertiaryAddress || '',
      tertiaryDateGraduated: student.tertiaryDateGraduated || '',
      tertiaryCourse: student.tertiaryCourse || '',
      seniorHighSchool: student.seniorHighSchool || '',
      seniorHighAddress: student.seniorHighAddress || '',
      seniorHighDateGraduated: student.seniorHighDateGraduated || '',
      seniorHighCourse: student.seniorHighCourse || '',
      juniorHighSchool: student.juniorHighSchool || '',
      juniorHighAddress: student.juniorHighAddress || '',
      juniorHighDateGraduated: student.juniorHighDateGraduated || '',
      elementarySchool: student.elementarySchool || '',
      elementaryAddress: student.elementaryAddress || '',
      elementaryDateGraduated: student.elementaryDateGraduated || '',
    });
    setProfileImageFile(null);
    setProfileImagePreview(student.profileImage ? getImageUrl(student.profileImage) : null);
    setModalError('');
    setShowStudentModal(true);
  };

  const handleSaveStudent = async (e) => {
    e.preventDefault();
    setModalError('');

    try {
      let profileImageUrl = studentForm.profileImage;
      
      // Upload profile image if a new file was selected
      if (profileImageFile) {
        setUploadingImage(true);
        try {
          const uploadResponse = await uploadAPI.uploadProfileImage(profileImageFile);
          profileImageUrl = uploadResponse.data.data.url;
        } catch (uploadError) {
          setModalError('Failed to upload profile image. Please try again.');
          setUploadingImage(false);
          return;
        }
        setUploadingImage(false);
      }
      
      const studentData = { ...studentForm, profileImage: profileImageUrl };
      
      // Convert MM/DD/YYYY to YYYY-MM-DD for backend
      if (studentData.dateOfBirth && studentData.dateOfBirth.includes('/')) {
        const [mm, dd, yyyy] = studentData.dateOfBirth.split('/');
        if (mm && dd && yyyy && yyyy.length === 4) {
          studentData.dateOfBirth = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
        }
      }
      
      if (editingStudent) {
        await adminAPI.updateStudent(editingStudent.id, studentData);
        setSuccessMessage('Student updated successfully!');
      } else {
        await adminAPI.createStudent(studentData);
        setSuccessMessage('Student created successfully!');
      }
      setShowStudentModal(false);
      setProfileImageFile(null);
      setProfileImagePreview(null);
      loadStudents();
      loadDashboardData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setModalError(error.response?.data?.message || 'Failed to save student');
    }
  };

  const handleDeleteStudent = async (student) => {
    if (window.confirm(`Are you sure you want to delete ${student.firstName} ${student.lastName}?`)) {
      try {
        await adminAPI.deleteStudent(student.id);
        setSuccessMessage('Student deleted successfully!');
        loadStudents();
        loadDashboardData();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete student');
      }
    }
  };

  const handleForceLogoutStudent = async (student) => {
    if (window.confirm(`Sign out ${student.firstName} ${student.lastName}? This will end their current session and they will need to log in again.`)) {
      try {
        const response = await adminAPI.forceLogoutStudent(student.studentId);
        setSuccessMessage(response.data.message || 'Student signed out successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to sign out student');
      }
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setModalError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setModalError('File size too large. Maximum size is 5MB.');
        return;
      }
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
      setModalError('');
    }
  };

  const handleRemoveImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(null);
    setStudentForm({ ...studentForm, profileImage: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Teacher image handlers
  const handleTeacherImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setModalError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setModalError('File size too large. Maximum size is 5MB.');
        return;
      }
      setTeacherProfileImageFile(file);
      setTeacherProfileImagePreview(URL.createObjectURL(file));
      setModalError('');
    }
  };

  const handleRemoveTeacherImage = () => {
    setTeacherProfileImageFile(null);
    setTeacherProfileImagePreview(null);
    setTeacherForm({ ...teacherForm, profileImage: '' });
    if (teacherFileInputRef.current) {
      teacherFileInputRef.current.value = '';
    }
  };

  const handleCreateTeacher = () => {
    setEditingTeacher(null);
    setTeacherForm({
      employeeId: '',
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      password: 'teacher123',
      position: '',
      contactNumber: '',
      role: 'teacher',
      profileImage: '',
    });
    setTeacherProfileImageFile(null);
    setTeacherProfileImagePreview(null);
    setModalError('');
    setShowTeacherModal(true);
  };

  const handleEditTeacher = (teacher) => {
    setEditingTeacher(teacher);
    setTeacherForm({
      employeeId: teacher.employeeId,
      firstName: teacher.firstName,
      middleName: teacher.middleName || '',
      lastName: teacher.lastName,
      email: teacher.email,
      password: '',
      position: teacher.position || '',
      contactNumber: teacher.contactNumber || '',
      role: teacher.role,
      profileImage: teacher.profileImage || '',
    });
    setTeacherProfileImageFile(null);
    setTeacherProfileImagePreview(teacher.profileImage ? getImageUrl(teacher.profileImage) : null);
    setModalError('');
    setShowTeacherModal(true);
  };

  const handleSaveTeacher = async (e) => {
    e.preventDefault();
    setModalError('');

    try {
      let profileImageUrl = teacherForm.profileImage;
      
      // Upload profile image if a new file was selected
      if (teacherProfileImageFile) {
        setUploadingImage(true);
        try {
          const uploadResponse = await uploadAPI.uploadProfileImage(teacherProfileImageFile);
          profileImageUrl = uploadResponse.data.data.url;
        } catch (uploadError) {
          setModalError('Failed to upload profile image. Please try again.');
          setUploadingImage(false);
          return;
        }
        setUploadingImage(false);
      }
      
      const teacherData = { ...teacherForm, profileImage: profileImageUrl };
      
      if (editingTeacher) {
        await adminAPI.updateTeacher(editingTeacher.id, teacherData);
        setSuccessMessage('Teacher updated successfully!');
        // If updating the current user, refresh their data in the auth context
        if (editingTeacher.id === user?.id) {
          await refreshUser();
        }
      } else {
        await adminAPI.createTeacher(teacherData);
        setSuccessMessage('Teacher created successfully!');
      }
      setShowTeacherModal(false);
      setTeacherProfileImageFile(null);
      setTeacherProfileImagePreview(null);
      loadTeachers();
      loadDashboardData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setModalError(error.response?.data?.message || 'Failed to save teacher');
    }
  };

  const handleForceLogoutTeacher = async (teacher) => {
    if (window.confirm(`Sign out ${teacher.firstName} ${teacher.lastName}? This will end their current session and they will need to log in again.`)) {
      try {
        const response = await adminAPI.forceLogoutTeacher(teacher.employeeId);
        setSuccessMessage(response.data.message || 'Faculty member signed out successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to sign out faculty member');
      }
    }
  };

  const handleDeleteTeacher = async (teacher) => {
    if (window.confirm(`Are you sure you want to delete ${teacher.firstName} ${teacher.lastName}?`)) {
      try {
        await adminAPI.deleteTeacher(teacher.id);
        setSuccessMessage('Teacher deleted successfully!');
        loadTeachers();
        loadDashboardData();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete teacher');
      }
    }
  };

  const handleDeleteLog = async (logId) => {
    if (window.confirm('Are you sure you want to delete this log?')) {
      try {
        await adminAPI.deleteLog(logId);
        setSuccessMessage('Log deleted successfully!');
        setSelectedLogs(prev => prev.filter(id => id !== logId));
        loadActivityLogs();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete log');
      }
    }
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId) 
        : [...prev, logId]
    );
  };

  const handleSelectAllLogs = () => {
    if (selectedLogs.length === activityLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(activityLogs.map(log => log.id));
    }
  };

  const handleDeleteSelectedLogs = async () => {
    if (selectedLogs.length === 0) {
      alert('Please select logs to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedLogs.length} selected log(s)?`)) {
      try {
        await adminAPI.deleteSelectedLogs(selectedLogs);
        setSuccessMessage(`Successfully deleted ${selectedLogs.length} log(s)!`);
        setSelectedLogs([]);
        loadActivityLogs();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete selected logs');
      }
    }
  };

  const handleDeleteAllLogs = async () => {
    if (activityLogs.length === 0) {
      alert('No logs to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ALL ${logsPagination.total || activityLogs.length} activity logs? This action cannot be undone.`)) {
      try {
        const response = await adminAPI.deleteAllLogs();
        setSuccessMessage(`Successfully deleted ${response.data.deletedCount} log(s)!`);
        setSelectedLogs([]);
        loadActivityLogs();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete all logs');
      }
    }
  };

  const handleOpenResetPassword = (teacher) => {
    setResetPasswordTeacher(teacher);
    setNewPassword('');
    setModalError('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setModalError('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      setModalError('Password must be at least 6 characters');
      return;
    }

    try {
      await adminAPI.resetTeacherPassword(resetPasswordTeacher.id, newPassword);
      setSuccessMessage(`Password for ${resetPasswordTeacher.firstName} ${resetPasswordTeacher.lastName} has been reset successfully!`);
      setShowResetPasswordModal(false);
      setResetPasswordTeacher(null);
      setNewPassword('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setModalError(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleChangeOwnPassword = async () => {
    setModalError('');
    
    if (!changePasswordForm.currentPassword.trim()) {
      setModalError('Please enter your current password');
      return;
    }
    if (!changePasswordForm.newPassword.trim()) {
      setModalError('Please enter a new password');
      return;
    }
    if (changePasswordForm.newPassword.length < 6) {
      setModalError('New password must be at least 6 characters');
      return;
    }
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      setModalError('New passwords do not match');
      return;
    }

    try {
      await authAPI.changePassword({
        currentPassword: changePasswordForm.currentPassword,
        newPassword: changePasswordForm.newPassword
      });
      setSuccessMessage('Your password has been changed successfully!');
      setShowChangePasswordModal(false);
      setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setModalError(error.response?.data?.message || 'Failed to change password');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'Enrolled': 'badge-success',
      'Not Enrolled': 'badge-warning',
      'Graduated': 'badge-info',
      'Dropped': 'badge-error',
      'Leave of Absence': 'badge-warning',
      'Regular': 'badge-success',
      'Irregular': 'badge-warning',
      'Probation': 'badge-error',
      "Dean's List": 'badge-info',
      "President's List": 'badge-primary',
    };
    return classes[status] || 'badge-default';
  };

  return (
    <div className="admin-dashboard">
      {/* Mobile Menu Toggle */}
      <button 
        className={`mobile-menu-toggle ${mobileMenuOpen ? 'sidebar-open' : ''}`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <FiX /> : <FiMenu />}
      </button>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Success Message */}
      {successMessage && (
        <div className="success-toast">
          <FiCheck /> {successMessage}
        </div>
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img src="/logo.png" alt="CCS Admin Logo" className="sidebar-logo" />
            <div className="logo-text">
              <span className="logo-title">CCS Admin</span>
              <span className="logo-subtitle">Management System</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }}
          >
            <FiGrid /> Dashboard
          </button>
          <button 
            className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => { setActiveTab('analytics'); setMobileMenuOpen(false); }}
          >
            <FiBarChart2 /> Analytics
          </button>
          <button 
            className={`nav-item ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => { setActiveTab('students'); setMobileMenuOpen(false); }}
          >
            <FiUsers /> Students
          </button>
          <button 
            className={`nav-item ${activeTab === 'grades' ? 'active' : ''}`}
            onClick={() => { setActiveTab('grades'); setMobileMenuOpen(false); }}
          >
            <FiBook /> Grades
          </button>
          <button 
            className={`nav-item ${activeTab === 'subjects' ? 'active' : ''}`}
            onClick={() => { setActiveTab('subjects'); setMobileMenuOpen(false); }}
          >
            <FiList /> Subjects
          </button>
          <button 
            className={`nav-item ${activeTab === 'enrollments' ? 'active' : ''}`}
            onClick={() => { setActiveTab('enrollments'); setMobileMenuOpen(false); loadEnrollmentsData(); }}
          >
            <FiClipboard /> Enrollments
          </button>
          <button 
            className={`nav-item ${activeTab === 'teachers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('teachers'); setMobileMenuOpen(false); }}
          >
            <FiUser /> Faculty Members
          </button>
          <button 
            className={`nav-item ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => { setActiveTab('logs'); setMobileMenuOpen(false); }}
          >
            <FiActivity /> Activity Logs
          </button>
          <button 
            className="nav-item theme-toggle"
            onClick={toggleDarkMode}
          >
            {isDarkMode ? <FiSun /> : <FiMoon />} {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.profileImage ? (
                <img 
                  src={getImageUrl(user.profileImage)} 
                  alt={`${user.firstName} ${user.lastName}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : (
                <DefaultAvatar 
                  firstName={user?.firstName} 
                  lastName={user?.lastName} 
                  size="md"
                />
              )}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.firstName} {user?.lastName}</span>
              <span className="user-role">Administrator</span>
            </div>
          </div>
          <button 
            className="change-password-btn" 
            onClick={() => {
              setModalError('');
              setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setShowChangePasswordModal(true);
            }}
          >
            <FiLock /> Change Password
          </button>
          <button className="logout-btn" onClick={logout}>
            <FiLogOut /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="dashboard-content">
            <div className="page-header">
              <h1>Dashboard Overview</h1>
              <button className="btn-refresh" onClick={loadDashboardData}>
                <FiRefreshCw /> Refresh
              </button>
            </div>

            {dashboardData && (
              <>
                <div className="stats-cards">
                  <div className="stat-card primary">
                    <div className="stat-icon"><FiUsers /></div>
                    <div className="stat-content">
                      <span className="stat-value">{dashboardData.totalStudents}</span>
                      <span className="stat-label">4th Year Students</span>
                    </div>
                  </div>
                  <div className="stat-card secondary">
                    <div className="stat-icon"><FiUser /></div>
                    <div className="stat-content">
                      <span className="stat-value">{dashboardData.totalTeachers}</span>
                      <span className="stat-label">Total Faculty Members</span>
                    </div>
                  </div>
                  <div className="stat-card accent">
                    <div className="stat-icon"><FiMessageSquare /></div>
                    <div className="stat-content">
                      <span className="stat-value">{dashboardData.totalQueries}</span>
                      <span className="stat-label">Total Queries</span>
                    </div>
                  </div>
                </div>

                <div className="dashboard-grid">
                  <div className="dashboard-card">
                    <h3>Enrollment Status</h3>
                    <div className="status-list">
                      {Object.entries(dashboardData.studentsByEnrollment || {}).length > 0 ? (
                        Object.entries(dashboardData.studentsByEnrollment || {}).map(([status, count]) => (
                          <div key={status} className="status-item">
                            <span className={`badge ${getStatusBadgeClass(status)}`}>{status}</span>
                            <span className="status-count">{count}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No enrollment data available</p>
                      )}
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <h3>Academic Status</h3>
                    <div className="status-list">
                      {Object.entries(dashboardData.studentsByAcademic || {}).length > 0 ? (
                        Object.entries(dashboardData.studentsByAcademic || {}).map(([status, count]) => (
                          <div key={status} className="status-item">
                            <span className={`badge ${getStatusBadgeClass(status)}`}>{status}</span>
                            <span className="status-count">{count}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No academic data available</p>
                      )}
                    </div>
                  </div>

                  <div className="dashboard-card full-width">
                    <h3>Recent 4th Year Students</h3>
                    {(dashboardData.recentStudents || []).length > 0 ? (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Student ID</th>
                            <th>Name</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dashboardData.recentStudents || []).map((student) => (
                            <tr key={student.id}>
                              <td>{student.studentId}</td>
                              <td>{student.firstName} {student.lastName}{student.suffix ? ` ${student.suffix}` : ''}</td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(student.enrollmentStatus)}`}>
                                  {student.enrollmentStatus}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-muted">No students added yet</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsCharts dashboardData={{
            students: {
              total: dashboardData?.totalStudents || 0,
              byEnrollmentStatus: dashboardData?.studentsByEnrollment || {},
              byAcademicStatus: dashboardData?.studentsByAcademic || {}
            }
          }} />
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="students-content">
            <div className="page-header">
              <h1>Student Management</h1>
              <button className="btn-primary" onClick={handleCreateStudent}>
                <FiPlus /> Add Student
              </button>
            </div>

            <div className="filters-bar">
              <div className="search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search 4th year students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={filters.enrollmentStatus}
                onChange={(e) => setFilters({ ...filters, enrollmentStatus: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="Enrolled">Enrolled</option>
                <option value="Not Enrolled">Not Enrolled</option>
                <option value="Dropped">Dropped</option>
              </select>
            </div>

            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Photo</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td className="id-cell">{student.studentId}</td>
                      <td>
                        <div className="name-cell">
                          <span className="name">{student.firstName} {student.lastName}{student.suffix ? ` ${student.suffix}` : ''}</span>
                          {student.middleName && (
                            <span className="middle-name">{student.middleName}</span>
                          )}
                        </div>
                      </td>
                      <td>{student.email}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(student.enrollmentStatus)}`}>
                          {student.enrollmentStatus}
                        </span>
                      </td>
                      <td>
                        <div className="photo-thumbnail">
                          {student.profileImage ? (
                            <img 
                              src={getImageUrl(student.profileImage)} 
                              alt={student.firstName}
                              onClick={() => setImagePreviewModal({ 
                                show: true, 
                                src: getImageUrl(student.profileImage), 
                                alt: `${student.firstName} ${student.lastName}` 
                              })}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <DefaultAvatar 
                              firstName={student.firstName} 
                              lastName={student.lastName} 
                              size="full"
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon qr"
                            onClick={() => setQrCodeStudent(student)}
                            title="QR Code"
                          >
                            <BsQrCode />
                          </button>
                          <button 
                            className="btn-icon edit"
                            onClick={() => handleEditStudent(student)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>
                          <button 
                            className="btn-icon signout"
                            onClick={() => handleForceLogoutStudent(student)}
                            title="Sign Out Student"
                          >
                            <FiLogOut />
                          </button>
                          <button 
                            className="btn-icon delete"
                            onClick={() => handleDeleteStudent(student)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <span className="pagination-info">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} students
              </span>
              <div className="pagination-buttons">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  <FiChevronLeft />
                </button>
                <span>Page {pagination.page} of {pagination.totalPages}</span>
                <button
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Teachers Tab */}
        {activeTab === 'teachers' && (
          <div className="teachers-content">
            <div className="page-header">
              <h1>Faculty Members Management</h1>
              <button className="btn-primary" onClick={handleCreateTeacher}>
                <FiPlus /> Add Faculty Member
              </button>
            </div>

            <div className="filters-bar">
              <div className="search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search teachers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="photo-column">Photo</th>
                    <th>Employee ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Position</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher) => (
                    <tr key={teacher.id}>
                      <td className="photo-cell">
                        {teacher.profileImage ? (
                          <img 
                            src={getImageUrl(teacher.profileImage)}
                            alt={`${teacher.firstName}'s profile`}
                            className="table-profile-img"
                            onClick={() => setImagePreviewModal({ 
                              show: true, 
                              src: getImageUrl(teacher.profileImage), 
                              alt: `${teacher.firstName} ${teacher.lastName}` 
                            })}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <DefaultAvatar 
                            firstName={teacher.firstName} 
                            lastName={teacher.lastName} 
                            size="md"
                          />
                        )}
                      </td>
                      <td className="id-cell">{teacher.employeeId}</td>
                      <td>{teacher.firstName} {teacher.lastName}</td>
                      <td>{teacher.email}</td>
                      <td>{teacher.position || '-'}</td>
                      <td>
                        <span className={`badge badge-${teacher.role}`}>
                          {teacher.role === 'admin' ? 'Admin' :
                           teacher.role === 'department_head' ? 'Dept. Head' : 'Lecturer'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${teacher.isActive ? 'badge-success' : 'badge-error'}`}>
                          {teacher.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon edit"
                            onClick={() => handleEditTeacher(teacher)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>
                          <button 
                            className="btn-icon reset-password"
                            onClick={() => handleOpenResetPassword(teacher)}
                            title="Reset Password"
                            disabled={teacher.id === user?.id}
                          >
                            <FiKey />
                          </button>
                          <button 
                            className="btn-icon signout"
                            onClick={() => handleForceLogoutTeacher(teacher)}
                            title="Sign Out Faculty"
                            disabled={teacher.id === user?.id}
                          >
                            <FiLogOut />
                          </button>
                          <button 
                            className="btn-icon delete"
                            onClick={() => handleDeleteTeacher(teacher)}
                            title="Delete"
                            disabled={teacher.id === user?.id}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Logs Tab */}
        {activeTab === 'logs' && (
          <div className="logs-content">
            <div className="page-header">
              <h1>Activity Logs</h1>
              <div className="header-actions">
                {activityLogs.length > 0 && (
                  <>
                    <button 
                      className="btn-danger" 
                      onClick={handleDeleteSelectedLogs}
                      disabled={selectedLogs.length === 0}
                    >
                      <FiTrash2 /> Delete Selected ({selectedLogs.length})
                    </button>
                    <button 
                      className="btn-danger-outline" 
                      onClick={handleDeleteAllLogs}
                    >
                      <FiTrash2 /> Delete All
                    </button>
                  </>
                )}
                <button className="btn-refresh" onClick={loadActivityLogs}>
                  <FiRefreshCw /> Refresh
                </button>
              </div>
            </div>
            
            {logsLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading activity logs...</p>
              </div>
            ) : activityLogs.length === 0 ? (
              <div className="dashboard-card">
                <div className="empty-state">
                  <FiActivity className="empty-icon" />
                  <h3>No Activity Logs Yet</h3>
                  <p>Query logs will appear here when users interact with the chatbot.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="checkbox-col">
                          <input 
                            type="checkbox" 
                            checked={selectedLogs.length === activityLogs.length && activityLogs.length > 0}
                            onChange={handleSelectAllLogs}
                            title="Select All"
                          />
                        </th>
                        <th>Date & Time</th>
                        <th>User</th>
                        <th>Query</th>
                        <th>Type</th>
                        <th>Results</th>
                        <th>Response Time</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.map((log) => (
                        <tr key={log.id} className={selectedLogs.includes(log.id) ? 'selected' : ''}>
                          <td className="checkbox-col">
                            <input 
                              type="checkbox" 
                              checked={selectedLogs.includes(log.id)}
                              onChange={() => handleSelectLog(log.id)}
                            />
                          </td>
                          <td>
                            <div className="log-timestamp">
                              <span className="log-date">
                                {new Date(log.createdAt).toLocaleDateString()}
                              </span>
                              <span className="log-time">
                                {new Date(log.createdAt).toLocaleTimeString()}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="log-teacher">
                              {log.teacher ? (
                                <>
                                  <span className="teacher-name">
                                    {log.teacher.firstName} {log.teacher.lastName}
                                  </span>
                                  <span className="teacher-email">{log.teacher.email}</span>
                                  <span className={`badge ${log.teacher.userType === 'student' ? 'badge-info' : 'badge-success'}`} style={{fontSize: '0.7rem', marginTop: '2px'}}>
                                    {log.teacher.userType === 'student' ? 'Student' : 'Faculty'}
                                  </span>
                                </>
                              ) : (
                                <span className="unknown-user">Unknown User</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="log-query" title={log.query}>
                              {log.query.length > 50 ? log.query.substring(0, 50) + '...' : log.query}
                            </div>
                          </td>
                          <td>
                            <span className={`badge badge-${log.queryType || 'default'}`}>
                              {log.queryType || 'general'}
                            </span>
                          </td>
                          <td>
                            <span className="results-count">
                              {log.resultsCount !== null ? log.resultsCount : '-'}
                            </span>
                          </td>
                          <td>
                            <span className="response-time">
                              {log.responseTime ? `${log.responseTime}ms` : '-'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="btn-icon delete" 
                                onClick={() => handleDeleteLog(log.id)}
                                title="Delete Log"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Logs Pagination */}
                <div className="pagination">
                  <span className="pagination-info">
                    Showing {((logsPagination.page - 1) * logsPagination.limit) + 1} to{' '}
                    {Math.min(logsPagination.page * logsPagination.limit, logsPagination.total)} of {logsPagination.total} logs
                  </span>
                  <div className="pagination-buttons">
                    <button
                      disabled={logsPagination.page === 1}
                      onClick={() => setLogsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      <FiChevronLeft /> Previous
                    </button>
                    <span className="page-indicator">
                      Page {logsPagination.page} of {logsPagination.totalPages}
                    </span>
                    <button
                      disabled={logsPagination.page === logsPagination.totalPages}
                      onClick={() => setLogsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next <FiChevronRight />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Grades Tab */}
        {activeTab === 'grades' && (
          <div className="grades-content">
            <div className="page-header">
              <h1>Student Grades Management</h1>
            </div>

            {/* Semester, School Year, Year Level, and Grading Period Filters */}
            <div className="grades-filters">
              <div className="filter-group">
                <label>Year Level:</label>
                <select 
                  value={selectedYearLevel} 
                  onChange={(e) => handleYearLevelChange(e.target.value)}
                  className="filter-select"
                >
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Semester:</label>
                <select 
                  value={selectedSemester} 
                  onChange={(e) => handleSemesterChange(e.target.value)}
                  className="filter-select"
                >
                  <option value={1}>1st Semester</option>
                  <option value={2}>2nd Semester</option>
                  {selectedYearLevel === 4 && <option value={3}>Summer</option>}
                </select>
              </div>
              <div className="filter-group">
                <label>School Year:</label>
                <select 
                  value={selectedSchoolYear} 
                  onChange={(e) => handleSchoolYearChange(e.target.value)}
                  className="filter-select"
                  disabled={selectedYearLevel !== null}
                >
                  {selectedYearLevel !== null ? (
                    <option value={getSchoolYearFromYearLevel(selectedYearLevel)}>
                      {getSchoolYearFromYearLevel(selectedYearLevel)}
                    </option>
                  ) : (
                    schoolYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))
                  )}
                </select>
              </div>
              {/* Hide grading period filter for Summer/OJT since it has no periods */}
              {selectedSemester !== 3 && (
                <div className="filter-group">
                  <label>Grading Period:</label>
                  <select 
                    value={selectedPeriod || ''} 
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Periods</option>
                    <option value="prelim">Prelim</option>
                    <option value="midterm">Midterm</option>
                    <option value="prefinal">Pre-Final</option>
                    <option value="final">Final</option>
                  </select>
                </div>
              )}
              <div className="filter-group">
                <label>Subject:</label>
                <select 
                  value={selectedSubject || ''} 
                  onChange={(e) => handleSubjectChange(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Subjects</option>
                  {subjects
                    .filter(subject => 
                      // Filter by selected year level and semester
                      (selectedYearLevel === null || subject.yearLevel === selectedYearLevel) &&
                      (selectedSemester === null || subject.semester === selectedSemester)
                    )
                    .filter((subject, index, self) => 
                      index === self.findIndex(s => s.courseCode === subject.courseCode)
                    )
                    .sort((a, b) => a.courseCode.localeCompare(b.courseCode))
                    .map(subject => (
                      <option key={subject.courseCode} value={subject.courseCode}>
                        {subject.courseCode} - {subject.subjectTitle}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grades-layout">
              {/* Student List Panel */}
              <div className="grades-student-panel">
                <div className="panel-header">
                  <h3><FiUsers /> Select Student</h3>
                  <div className="search-box">
                    <FiSearch />
                    <input
                      type="text"
                      placeholder="Search students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="student-list-grades">
                  {students.filter(s => 
                    `${s.firstName} ${s.lastName} ${s.studentId}`.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map(student => (
                    <div 
                      key={student.id} 
                      className={`student-list-item ${selectedStudentForGrades?.id === student.id ? 'selected' : ''}`}
                      onClick={() => handleSelectStudentForGrades(student)}
                    >
                      <div className="student-avatar-small">
                        {student.profileImage ? (
                          <img src={getImageUrl(student.profileImage)} alt={student.firstName} />
                        ) : (
                          <DefaultAvatar 
                            firstName={student.firstName} 
                            lastName={student.lastName} 
                            size="md"
                          />
                        )}
                      </div>
                      <div className="student-info-compact">
                        <span className="student-name">{student.firstName} {student.lastName}{student.suffix ? ` ${student.suffix}` : ''}</span>
                        <span className="student-id">{student.studentId}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grades Panel */}
              <div className="grades-main-panel">
                {!selectedStudentForGrades ? (
                  <div className="empty-state">
                    <FiBook className="empty-icon" />
                    <h3>Select a Student</h3>
                    <p>Choose a student from the list to view and manage their grades.</p>
                  </div>
                ) : (
                  <>
                    <div className="grades-panel-header">
                      <div className="selected-student-info">
                        <div className="student-avatar-large">
                          {selectedStudentForGrades.profileImage ? (
                            <img src={getImageUrl(selectedStudentForGrades.profileImage)} alt={selectedStudentForGrades.firstName} />
                          ) : (
                            <DefaultAvatar 
                              firstName={selectedStudentForGrades.firstName} 
                              lastName={selectedStudentForGrades.lastName} 
                              size="lg"
                            />
                          )}
                        </div>
                        <div>
                          <h2>{selectedStudentForGrades.firstName} {selectedStudentForGrades.lastName}</h2>
                          <p>{selectedStudentForGrades.studentId}</p>
                        </div>
                      </div>
                      <div className="grades-actions">
                        <button className="btn-primary" onClick={handleAddGrade}>
                          <FiPlus /> Add Grade
                        </button>
                      </div>
                    </div>

                    {/* GWA Display */}
                    {studentGWA && (
                      <div className="gwa-card">
                        <div className="gwa-info">
                          <FiAward className="gwa-icon" />
                          <div>
                            <span className="gwa-label">General Weighted Average (GWA)</span>
                            <span className="gwa-value" style={{ color: getGradeColor(parseFloat(studentGWA.gwa)) }}>
                              {studentGWA.gwa}
                            </span>
                          </div>
                        </div>
                        <div className="gwa-stats">
                          <div className="gwa-stat">
                            <span className="stat-value">{studentGWA.totalUnits}</span>
                            <span className="stat-label">Total Units</span>
                          </div>
                          <div className="gwa-stat">
                            <span className="stat-value">{studentGWA.gradedCount}/{studentGWA.subjectCount}</span>
                            <span className="stat-label">Graded</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Grades Table */}
                    {gradesLoading ? (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading grades...</p>
                      </div>
                    ) : grades.length === 0 ? (
                      <div className="empty-state">
                        <FiBook className="empty-icon" />
                        <h3>No Grades Yet</h3>
                        <p>Click "Add Grade" to add grades for this student.</p>
                      </div>
                    ) : (
                      <>
                        {/* Subject Navigation */}
                        {getUniqueSubjects().length > 1 && (
                          <div className="subject-navigation">
                            <button 
                              className="nav-arrow prev"
                              onClick={() => setCurrentSubjectIndex(prev => Math.max(0, prev - 1))}
                              disabled={currentSubjectIndex === 0}
                            >
                              <FiChevronLeft />
                            </button>
                            <div className="subject-nav-info">
                              <span className="subject-counter">
                                Subject {currentSubjectIndex + 1} of {getUniqueSubjects().length}
                              </span>
                              <span className="current-subject-name">
                                {getUniqueSubjects()[currentSubjectIndex]?.courseCode} - {getUniqueSubjects()[currentSubjectIndex]?.subjectTitle}
                              </span>
                            </div>
                            <button 
                              className="nav-arrow next"
                              onClick={() => setCurrentSubjectIndex(prev => Math.min(getUniqueSubjects().length - 1, prev + 1))}
                              disabled={currentSubjectIndex === getUniqueSubjects().length - 1}
                            >
                              <FiChevronRight />
                            </button>
                          </div>
                        )}
                        <div className="data-table-container">
                        <table className="data-table grades-table">
                          <thead>
                            <tr>
                              <th>Course Code</th>
                              <th>Subject Title</th>
                              <th>Section</th>
                              <th>Units</th>
                              <th>Year Level</th>
                              <th>Period</th>
                              <th>Grade</th>
                              <th>Final Grade</th>
                              <th>Remarks</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const uniqueSubjects = getUniqueSubjects();
                              const subjectsToShow = uniqueSubjects.length > 1 
                                ? [uniqueSubjects[currentSubjectIndex]] 
                                : uniqueSubjects;
                              
                              return subjectsToShow.map(subject => {
                                const finalGrade = calculateFinalGrade(
                                  subject.courseCode, 
                                  subject.semester, 
                                  subject.yearLevel, 
                                  subject.schoolYear
                                );
                                
                                const subjectGrades = grades.filter(g => 
                                  g.courseCode === subject.courseCode && 
                                  g.semester === subject.semester && 
                                  g.yearLevel === subject.yearLevel &&
                                  g.schoolYear === subject.schoolYear
                                ).sort((a, b) => {
                                  const order = { prelim: 1, midterm: 2, prefinal: 3, final: 4, ojt: 5 };
                                  return order[a.period] - order[b.period];
                                });
                                
                                return subjectGrades.map((grade, index) => (
                                <tr key={grade.id}>
                                  {index === 0 && (
                                    <>
                                      <td rowSpan={subjectGrades.length}><strong>{grade.courseCode}</strong></td>
                                      <td rowSpan={subjectGrades.length}>{grade.subjectTitle}</td>
                                      <td rowSpan={subjectGrades.length} className="text-center">
                                        <span className="section-badge">{grade.section || '-'}</span>
                                      </td>
                                      <td rowSpan={subjectGrades.length} className="text-center">{grade.units}</td>
                                      <td rowSpan={subjectGrades.length} className="text-center">
                                        <span className="badge badge-info">
                                          {grade.yearLevel === 1 ? '1st' : grade.yearLevel === 2 ? '2nd' : grade.yearLevel === 3 ? '3rd' : '4th'} Year
                                        </span>
                                      </td>
                                    </>
                                  )}
                                  <td className="text-center">
                                    <span className="badge badge-secondary">
                                      {grade.period === 'prelim' ? 'Prelim' : 
                                       grade.period === 'midterm' ? 'Midterm' : 
                                       grade.period === 'prefinal' ? 'Pre-Final' : 
                                       grade.period === 'ojt' ? 'OJT' : 'Final'}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span 
                                      className="grade-value" 
                                      style={{ color: getGradeColor(grade.grade) }}
                                    >
                                      {grade.grade !== null ? (typeof grade.grade === 'number' ? grade.grade.toFixed(2) : grade.grade) : 'N/A'}
                                    </span>
                                  </td>
                                  {index === 0 && (
                                    <td rowSpan={subjectGrades.length} className="text-center">
                                      {finalGrade ? (
                                        <span 
                                          className="grade-value" 
                                          style={{ 
                                            color: getGradeColor(parseFloat(finalGrade)),
                                            fontSize: '1.1em',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {finalGrade}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#999' }}>-</span>
                                      )}
                                    </td>
                                  )}
                                  <td>
                                    <span className={`badge badge-${grade.remarks?.toLowerCase().replace(' ', '-') || 'default'}`}>
                                      {grade.remarks || 'Not Graded'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="action-buttons">
                                      <button 
                                        className="btn-icon edit" 
                                        onClick={() => handleEditGrade(grade)}
                                        title="Edit Grade"
                                      >
                                        <FiEdit2 />
                                      </button>
                                      <button 
                                        className="btn-icon delete" 
                                        onClick={() => handleDeleteGrade(grade.id)}
                                        title="Delete Grade"
                                      >
                                        <FiTrash2 />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ));
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="subjects-content">
            <div className="page-header">
              <h1>Subjects Management</h1>
              <div className="header-actions">
                <button className="btn-primary" onClick={handleAddSubject}>
                  <FiPlus /> Add Subject
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="grades-filters">
              <div className="filter-group">
                <label>Year Level:</label>
                <select 
                  value={subjectsYearLevelFilter || ''} 
                  onChange={(e) => handleYearLevelChangeForSubjects(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Years</option>
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Semester:</label>
                <select 
                  value={subjectsSemesterFilter || ''} 
                  onChange={(e) => handleSemesterChangeForSubjects(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Semesters</option>
                  <option value={1}>1st Semester</option>
                  <option value={2}>2nd Semester</option>
                  {subjectsYearLevelFilter === 4 && <option value={3}>Summer</option>}
                </select>
              </div>
            </div>

            {/* Subjects Table */}
            <div className="content-card">
              {subjectsLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading subjects...</p>
                </div>
              ) : subjects.length === 0 ? (
                <div className="empty-state">
                  <FiBook className="empty-icon" />
                  <h3>No Subjects Found</h3>
                  <p>Click "Add Subject" to create a new subject.</p>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Course Code</th>
                        <th>Subject Title</th>
                        <th>Units</th>
                        <th>Year Level</th>
                        <th>Semester</th>
                        <th>Section</th>
                        <th>Schedule</th>
                        <th>Room</th>
                        <th>Instructor</th>
                        <th>Pre-requisite</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Group subjects by courseCode */}
                      {Object.entries(
                        subjects.reduce((groups, subject) => {
                          const key = subject.courseCode;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push(subject);
                          return groups;
                        }, {})
                      ).map(([courseCode, groupedSubjects]) => (
                        groupedSubjects.map((subject, index) => (
                          <tr 
                            key={subject.id} 
                            className={index > 0 ? 'grouped-row' : 'group-header-row'}
                          >
                            {/* Only show course code and title for first row in group */}
                            {index === 0 ? (
                              <>
                                <td rowSpan={groupedSubjects.length}>
                                  <strong>{subject.courseCode}</strong>
                                  {groupedSubjects.length > 1 && (
                                    <span className="section-count-badge">
                                      {groupedSubjects.length} sections
                                    </span>
                                  )}
                                </td>
                                <td rowSpan={groupedSubjects.length}>{subject.subjectTitle}</td>
                                <td rowSpan={groupedSubjects.length} className="text-center">{subject.units}</td>
                                <td rowSpan={groupedSubjects.length} className="text-center">
                                  <span className="badge badge-info">
                                    {subject.yearLevel === 1 ? '1st' : subject.yearLevel === 2 ? '2nd' : subject.yearLevel === 3 ? '3rd' : '4th'} Year
                                  </span>
                                </td>
                                <td rowSpan={groupedSubjects.length} className="text-center">
                                  {subject.semester === 1 ? '1st Sem' : subject.semester === 2 ? '2nd Sem' : 'Summer'}
                                </td>
                              </>
                            ) : null}
                            <td className="text-center">
                              <span className="section-badge">{subject.section || '-'}</span>
                            </td>
                            <td className="schedule-cell">
                              {subject.scheduleDay && subject.scheduleTimeStart && subject.scheduleTimeEnd ? (
                                <>
                                  <span className="schedule-day">{subject.scheduleDay}</span>
                                  <span className="schedule-time">
                                    {formatTimeTo12Hour(subject.scheduleTimeStart)} - {formatTimeTo12Hour(subject.scheduleTimeEnd)}
                                  </span>
                                </>
                              ) : '-'}
                            </td>
                            <td className="text-center">
                              <span className="room-badge">{subject.room || '-'}</span>
                            </td>
                            <td>
                              {subject.instructorFirstName && subject.instructorLastName 
                                ? `${subject.instructorFirstName} ${subject.instructorLastName}`
                                : '-'}
                            </td>
                            {index === 0 ? (
                              <td rowSpan={groupedSubjects.length}>{subject.preRequisite || '-'}</td>
                            ) : null}
                            <td>
                              <div className="action-buttons">
                                <button 
                                  className="btn-icon duplicate" 
                                  onClick={() => handleDuplicateSubject(subject)}
                                  title="Duplicate (Create New Section)"
                                >
                                  <FiCopy />
                                </button>
                                <button 
                                  className="btn-icon edit" 
                                  onClick={() => handleEditSubject(subject)}
                                  title="Edit Subject"
                                >
                                  <FiEdit2 />
                                </button>
                                <button 
                                  className="btn-icon delete" 
                                  onClick={() => handleDeleteSubject(subject.id)}
                                  title="Delete Subject"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enrollments Tab */}
        {activeTab === 'enrollments' && (
          <div className="enrollments-content">
            <div className="page-header">
              <h1>Enrollment Management</h1>
              <p className="subtitle">Enroll students in subject sections</p>
            </div>

            {/* Filters */}
            <div className="enrollments-filters">
              <div className="filter-group">
                <label>Year Level:</label>
                <select
                  value={enrollmentFilters.yearLevel || ''}
                  onChange={(e) => handleEnrollmentFilterChange('yearLevel', e.target.value ? parseInt(e.target.value) : null)}
                  className="filter-select"
                >
                  <option value="">All Years</option>
                  <option value="1">1st Year</option>
                  <option value="2">2nd Year</option>
                  <option value="3">3rd Year</option>
                  <option value="4">4th Year</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Semester:</label>
                <select
                  value={enrollmentFilters.semester || ''}
                  onChange={(e) => handleEnrollmentFilterChange('semester', e.target.value ? parseInt(e.target.value) : null)}
                  className="filter-select"
                >
                  <option value="">All Semesters</option>
                  <option value="1">1st Semester</option>
                  <option value="2">2nd Semester</option>
                  {enrollmentFilters.yearLevel === 4 && <option value="3">Summer (OJT)</option>}
                </select>
              </div>
              <button className="btn-primary" onClick={loadEnrollmentsData}>
                <FiRefreshCw /> Refresh
              </button>
            </div>

            {/* Subjects with Sections - Click to Enroll Students */}
            <div className="content-card">
              <div className="card-header">
                <h2><FiBook /> Subjects & Sections</h2>
                <p>Click "Enroll Students" to add students to a section</p>
              </div>
              
              {subjects.length === 0 ? (
                <div className="empty-state">
                  <FiBook className="empty-icon" />
                  <h3>No Subjects Found</h3>
                  <p>Add subjects first in the Subjects tab.</p>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Course Code</th>
                        <th>Subject Title</th>
                        <th>Year Level</th>
                        <th>Semester</th>
                        <th>Section</th>
                        <th>Instructor</th>
                        <th>Enrolled</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Group subjects by courseCode */}
                      {Object.entries(
                        getSubjectsWithSections().reduce((groups, subject) => {
                          const key = subject.courseCode;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push(subject);
                          return groups;
                        }, {})
                      ).map(([courseCode, groupedSubjects]) => (
                        groupedSubjects.map((subject, index) => {
                          const enrolledCount = enrollments.filter(e => 
                            e.courseCode === subject.courseCode && 
                            e.section === subject.section &&
                            e.semester === subject.semester &&
                            e.yearLevel === subject.yearLevel
                          ).length;
                          
                          return (
                            <tr 
                              key={subject.id}
                              className={index > 0 ? 'grouped-row' : 'group-header-row'}
                            >
                              {/* Only show course code, title, year level, semester for first row */}
                              {index === 0 ? (
                                <>
                                  <td rowSpan={groupedSubjects.length}>
                                    <strong>{subject.courseCode}</strong>
                                    {groupedSubjects.length > 1 && (
                                      <span className="section-count-badge">
                                        {groupedSubjects.length} sections
                                      </span>
                                    )}
                                  </td>
                                  <td rowSpan={groupedSubjects.length}>{subject.subjectTitle}</td>
                                  <td rowSpan={groupedSubjects.length} className="text-center">
                                    <span className="badge badge-info">
                                      {subject.yearLevel === 1 ? '1st' : subject.yearLevel === 2 ? '2nd' : subject.yearLevel === 3 ? '3rd' : '4th'} Year
                                    </span>
                                  </td>
                                  <td rowSpan={groupedSubjects.length} className="text-center">
                                    {subject.semester === 1 ? '1st Sem' : subject.semester === 2 ? '2nd Sem' : 'Summer'}
                                  </td>
                                </>
                              ) : null}
                              <td>
                                <span className="section-badge">{subject.section || '-'}</span>
                              </td>
                              <td>
                                {subject.instructorFirstName && subject.instructorLastName 
                                  ? `${subject.instructorFirstName} ${subject.instructorLastName}`
                                  : '-'}
                              </td>
                              <td className="text-center">
                                <span className={`badge ${enrolledCount > 0 ? 'badge-success' : 'badge-secondary'}`}>
                                  {enrolledCount} students
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button 
                                    className="btn-icon edit" 
                                    onClick={() => handleEnrollStudent(subject)}
                                    title="Enroll Students"
                                  >
                                    <FiUserCheck />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Current Enrollments */}
            <div className="content-card">
              <div className="card-header">
                <h2><FiClipboard /> Current Enrollments</h2>
                <div className="pagination-header">
                  <button 
                    className="btn-icon"
                    disabled={enrollmentsPagination.page === 1}
                    onClick={() => setEnrollmentsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    title="Previous"
                  >
                    <FiChevronLeft />
                  </button>
                  <span className="badge badge-info">{enrollments.length} total</span>
                  <button 
                    className="btn-icon"
                    disabled={enrollmentsPagination.page >= Math.ceil(enrollments.length / enrollmentsPagination.limit)}
                    onClick={() => setEnrollmentsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    title="Next"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
              
              {enrollments.length === 0 ? (
                <div className="empty-state">
                  <FiClipboard className="empty-icon" />
                  <h3>No Enrollments Yet</h3>
                  <p>Click "Enroll Students" on a subject section above to start enrolling students.</p>
                </div>
              ) : (
                <>
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Course Code</th>
                        <th>Subject</th>
                        <th>Section</th>
                        <th>Class #</th>
                        <th>Student ID</th>
                        <th>Student Name</th>
                        <th>Year/Sem</th>
                        <th>School Year</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Group enrollments by courseCode and section */}
                      {(() => {
                        // Sort and group enrollments
                        const sortedEnrollments = [...enrollments].sort((a, b) => {
                          // First sort by courseCode
                          if (a.courseCode !== b.courseCode) return a.courseCode.localeCompare(b.courseCode);
                          // Then by section
                          if (a.section !== b.section) return (a.section || '').localeCompare(b.section || '');
                          // Then by classNumber
                          return (a.classNumber || 999) - (b.classNumber || 999);
                        });
                        
                        // Paginate the sorted enrollments
                        const paginatedEnrollments = sortedEnrollments.slice(
                          (enrollmentsPagination.page - 1) * enrollmentsPagination.limit,
                          enrollmentsPagination.page * enrollmentsPagination.limit
                        );
                        
                        // Group by courseCode + section
                        const grouped = paginatedEnrollments.reduce((groups, enrollment) => {
                          const key = `${enrollment.courseCode}-${enrollment.section}`;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push(enrollment);
                          return groups;
                        }, {});
                        
                        return Object.entries(grouped).map(([groupKey, groupedEnrollments]) => (
                          groupedEnrollments.map((enrollment, index) => {
                            const student = students.find(s => s.studentId === enrollment.studentId);
                            return (
                              <tr 
                                key={enrollment.id}
                                className={index > 0 ? 'grouped-row' : 'group-header-row'}
                              >
                                {/* Only show course code, subject, section for first row in group */}
                                {index === 0 ? (
                                  <>
                                    <td rowSpan={groupedEnrollments.length}>
                                      <strong>{enrollment.courseCode}</strong>
                                    </td>
                                    <td rowSpan={groupedEnrollments.length}>{enrollment.subjectTitle}</td>
                                    <td rowSpan={groupedEnrollments.length}>
                                      <span className="section-badge">{enrollment.section}</span>
                                      <span className="section-count-badge">
                                        {groupedEnrollments.length} enrolled
                                      </span>
                                    </td>
                                  </>
                                ) : null}
                                <td className="text-center">
                                  <span className="class-number-badge">
                                    {enrollment.classNumber || '-'}
                                  </span>
                                </td>
                                <td><strong>{enrollment.studentId}</strong></td>
                                <td>{student ? `${student.firstName} ${student.lastName}${student.suffix ? ` ${student.suffix}` : ''}` : '-'}</td>
                                <td className="text-center">
                                  <span className="badge badge-info">
                                    Y{enrollment.yearLevel} S{enrollment.semester}
                                  </span>
                                </td>
                                <td>{enrollment.schoolYear}</td>
                                <td>
                                  <div className="action-buttons">
                                    <button 
                                      className="btn-icon edit" 
                                      onClick={() => handleEditEnrollment(enrollment)}
                                      title="Edit Class Number"
                                    >
                                      <FiEdit2 />
                                    </button>
                                    <button 
                                      className="btn-icon delete" 
                                      onClick={() => handleDeleteEnrollment(enrollment.id)}
                                      title="Remove Enrollment"
                                    >
                                      <FiTrash2 />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
                <div className="pagination-footer">
                  <span className="pagination-info">
                    Showing {((enrollmentsPagination.page - 1) * enrollmentsPagination.limit) + 1} to{' '}
                    {Math.min(enrollmentsPagination.page * enrollmentsPagination.limit, enrollments.length)} of {enrollments.length} enrollments
                  </span>
                  <span className="pagination-pages">
                    Page {enrollmentsPagination.page} of {Math.ceil(enrollments.length / enrollmentsPagination.limit) || 1}
                  </span>
                </div>
                </>
              )}
            </div>
          </div>
        )}

      </main>

      {/* Student Modal */}
      {showStudentModal && (
        <div className="modal-overlay" onClick={() => setShowStudentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingStudent ? 'Edit Student' : 'Add New Student'}</h2>
              <button className="close-btn" onClick={() => setShowStudentModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}
            
            <form onSubmit={handleSaveStudent} className="modal-form">
              {/* Profile Image Upload Section */}
              <div className="profile-image-section">
                <div className="profile-image-preview">
                  {profileImagePreview ? (
                    <img src={profileImagePreview} alt="Profile preview" />
                  ) : (
                    <div className="no-image">
                      <DefaultAvatar 
                        firstName={studentForm.firstName} 
                        lastName={studentForm.lastName} 
                        size="xxl"
                      />
                    </div>
                  )}
                </div>
                <div className="profile-image-actions">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    <FiCamera /> {profileImagePreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {profileImagePreview && (
                    <button
                      type="button"
                      className="btn-remove-image"
                      onClick={handleRemoveImage}
                      disabled={uploadingImage}
                    >
                      <FiX /> Remove
                    </button>
                  )}
                  <span className="image-hint">Max 5MB. JPEG, PNG, GIF, WebP</span>
                </div>
              </div>

              <div className="form-grid">
                {/* Student Type */}
                <div className="form-group">
                  <label>Student Type</label>
                  <select
                    value={studentForm.studentType}
                    onChange={(e) => setStudentForm({ ...studentForm, studentType: e.target.value })}
                  >
                    <option value="">Select Type</option>
                    <option value="New">New</option>
                    <option value="Old">Old</option>
                    <option value="Transferee">Transferee</option>
                    <option value="Cross Enrollee">Cross Enrollee</option>
                    <option value="Earning Units">Earning Units</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Student ID *</label>
                  <input
                    type="text"
                    value={studentForm.studentId}
                    onChange={(e) => setStudentForm({ ...studentForm, studentId: e.target.value })}
                    placeholder="CCS20240001"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Course / Program</label>
                  <input
                    type="text"
                    value={studentForm.program}
                    onChange={(e) => setStudentForm({ ...studentForm, program: e.target.value })}
                    placeholder="Bachelor of Science in Computer Science"
                  />
                </div>
                <div className="form-group">
                  <label>Year Level *</label>
                  <select
                    value={studentForm.yearLevel}
                    onChange={(e) => setStudentForm({ ...studentForm, yearLevel: parseInt(e.target.value) })}
                    required
                    disabled
                  >
                    <option value={4}>4th Year</option>
                  </select>
                  <small style={{ color: '#888', fontSize: '11px' }}>System is exclusive for 4th Year students</small>
                </div>

                {/* Student Name */}
                <div className="form-section-divider">
                  <span>Student Name</span>
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={studentForm.lastName}
                    onChange={(e) => setStudentForm({ ...studentForm, lastName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={studentForm.firstName}
                    onChange={(e) => setStudentForm({ ...studentForm, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Middle Name</label>
                  <input
                    type="text"
                    value={studentForm.middleName}
                    onChange={(e) => setStudentForm({ ...studentForm, middleName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Suffix</label>
                  <input
                    type="text"
                    value={studentForm.suffix}
                    onChange={(e) => setStudentForm({ ...studentForm, suffix: e.target.value })}
                    placeholder="Jr., Sr., III"
                  />
                </div>

                {/* Student's Profile */}
                <div className="form-section-divider">
                  <span>Student's Profile</span>
                </div>
                <div className="form-group">
                  <label>Learner's Reference Number</label>
                  <input
                    type="text"
                    value={studentForm.learnerReferenceNumber}
                    onChange={(e) => setStudentForm({ ...studentForm, learnerReferenceNumber: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date of Birth (MM/DD/YYYY)</label>
                  <input
                    type="text"
                    value={studentForm.dateOfBirth}
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^\d]/g, '');
                      if (val.length > 8) val = val.slice(0, 8);
                      if (val.length >= 5) val = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4);
                      else if (val.length >= 3) val = val.slice(0, 2) + '/' + val.slice(2);
                      setStudentForm({ ...studentForm, dateOfBirth: val });
                    }}
                    placeholder="MM/DD/YYYY"
                    maxLength={10}
                  />
                </div>
                <div className="form-group">
                  <label>Birthplace</label>
                  <input
                    type="text"
                    value={studentForm.birthplace}
                    onChange={(e) => setStudentForm({ ...studentForm, birthplace: e.target.value })}
                    placeholder="City/Municipality, Province"
                  />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    value={studentForm.gender}
                    onChange={(e) => setStudentForm({ ...studentForm, gender: e.target.value })}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Contact Number</label>
                  <input
                    type="text"
                    value={studentForm.contactNumber}
                    onChange={(e) => setStudentForm({ ...studentForm, contactNumber: e.target.value })}
                    placeholder="09XX-XXX-XXXX"
                  />
                </div>
                <div className="form-group">
                  <label>Civil Status</label>
                  <select
                    value={studentForm.civilStatus}
                    onChange={(e) => setStudentForm({ ...studentForm, civilStatus: e.target.value })}
                  >
                    <option value="">Select Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Separated">Separated</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Masbate City Address (Street/Purok/Sitio/Barangay)</label>
                  <input
                    type="text"
                    value={studentForm.masbateCityAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, masbateCityAddress: e.target.value })}
                    placeholder="Street/Purok/Sitio/Barangay"
                  />
                </div>
                <div className="form-group">
                  <label>Type of City Residence</label>
                  <select
                    value={studentForm.cityResidenceType}
                    onChange={(e) => setStudentForm({ ...studentForm, cityResidenceType: e.target.value })}
                  >
                    <option value="">Select Type</option>
                    <option value="Living with Relative/Parents">Living with Relative/Parents</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Boarding House">Boarding House</option>
                    <option value="Board and Lodging">Board and Lodging</option>
                    <option value="Own">Own</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={studentForm.email}
                    onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                    placeholder="student@oc.edu.ph"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Facebook Account</label>
                  <input
                    type="text"
                    value={studentForm.fbAccount}
                    onChange={(e) => setStudentForm({ ...studentForm, fbAccount: e.target.value })}
                    placeholder="facebook.com/username"
                  />
                </div>
                <div className="form-group">
                  <label>Enrollment Status</label>
                  <select
                    value={studentForm.enrollmentStatus}
                    onChange={(e) => setStudentForm({ ...studentForm, enrollmentStatus: e.target.value })}
                  >
                    <option value="Enrolled">Enrolled</option>
                    <option value="Not Enrolled">Not Enrolled</option>
                    <option value="Dropped">Dropped</option>
                    <option value="Leave of Absence">Leave of Absence</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Academic Status</label>
                  <select
                    value={studentForm.academicStatus}
                    onChange={(e) => setStudentForm({ ...studentForm, academicStatus: e.target.value })}
                  >
                    <option value="Regular">Regular</option>
                    <option value="Irregular">Irregular</option>
                    <option value="Probation">Probation</option>
                    <option value="Dean's List">Dean's List</option>
                    <option value="President's List">President's List</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Enrollment Date</label>
                  <input
                    type="date"
                    value={studentForm.enrollmentDate ? studentForm.enrollmentDate.split('T')[0] : ''}
                    onChange={(e) => setStudentForm({ ...studentForm, enrollmentDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Expected Graduation</label>
                  <input
                    type="date"
                    value={studentForm.expectedGraduationDate ? studentForm.expectedGraduationDate.split('T')[0] : ''}
                    onChange={(e) => setStudentForm({ ...studentForm, expectedGraduationDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Guardian Name</label>
                  <input
                    type="text"
                    value={studentForm.guardianName}
                    onChange={(e) => setStudentForm({ ...studentForm, guardianName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Guardian Contact</label>
                  <input
                    type="text"
                    value={studentForm.guardianContact}
                    onChange={(e) => setStudentForm({ ...studentForm, guardianContact: e.target.value })}
                    placeholder="09XX-XXX-XXXX"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Guardian Address</label>
                  <input
                    type="text"
                    value={studentForm.guardianAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, guardianAddress: e.target.value })}
                    placeholder="Complete address"
                  />
                </div>

                {/* Family Background Information */}
                <div className="form-section-divider">
                  <span>Family Background Information</span>
                </div>

                {/* Father's Information */}
                <div className="form-section-subtitle">
                  <span>Father's Information</span>
                </div>
                <div className="form-group">
                  <label>Father's Full Name</label>
                  <input
                    type="text"
                    value={studentForm.fatherName}
                    onChange={(e) => setStudentForm({ ...studentForm, fatherName: e.target.value })}
                    placeholder="First Name, Middle Name, Last Name"
                  />
                </div>
                <div className="form-group">
                  <label>Father's Occupation</label>
                  <input
                    type="text"
                    value={studentForm.fatherOccupation}
                    onChange={(e) => setStudentForm({ ...studentForm, fatherOccupation: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Father's Contact Number</label>
                  <input
                    type="text"
                    value={studentForm.fatherContactNumber}
                    onChange={(e) => setStudentForm({ ...studentForm, fatherContactNumber: e.target.value })}
                    placeholder="09XX-XXX-XXXX"
                  />
                </div>
                <div className="form-group">
                  <label>Father's Facebook Account</label>
                  <input
                    type="text"
                    value={studentForm.fatherFbAccount}
                    onChange={(e) => setStudentForm({ ...studentForm, fatherFbAccount: e.target.value })}
                    placeholder="facebook.com/username"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Father's Address</label>
                  <input
                    type="text"
                    value={studentForm.fatherAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, fatherAddress: e.target.value })}
                    placeholder="Complete address"
                  />
                </div>

                {/* Mother's Information */}
                <div className="form-section-subtitle">
                  <span>Mother's Information</span>
                </div>
                <div className="form-group">
                  <label>Mother's Full Name (Maiden Name)</label>
                  <input
                    type="text"
                    value={studentForm.motherMaidenName}
                    onChange={(e) => setStudentForm({ ...studentForm, motherMaidenName: e.target.value })}
                    placeholder="First Name, Middle Name, Last Name"
                  />
                </div>
                <div className="form-group">
                  <label>Mother's Occupation</label>
                  <input
                    type="text"
                    value={studentForm.motherOccupation}
                    onChange={(e) => setStudentForm({ ...studentForm, motherOccupation: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Mother's Contact Number</label>
                  <input
                    type="text"
                    value={studentForm.motherContactNumber}
                    onChange={(e) => setStudentForm({ ...studentForm, motherContactNumber: e.target.value })}
                    placeholder="09XX-XXX-XXXX"
                  />
                </div>
                <div className="form-group">
                  <label>Mother's Facebook Account</label>
                  <input
                    type="text"
                    value={studentForm.motherFbAccount}
                    onChange={(e) => setStudentForm({ ...studentForm, motherFbAccount: e.target.value })}
                    placeholder="facebook.com/username"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Mother's Address</label>
                  <input
                    type="text"
                    value={studentForm.motherAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, motherAddress: e.target.value })}
                    placeholder="Complete address"
                  />
                </div>

                {/* Additional Family Info */}
                <div className="form-section-subtitle">
                  <span>Additional Family Details</span>
                </div>
                <div className="form-group">
                  <label>Number of Siblings (including you)</label>
                  <input
                    type="number"
                    value={studentForm.numberOfSiblings}
                    onChange={(e) => setStudentForm({ ...studentForm, numberOfSiblings: e.target.value })}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Birth Order</label>
                  <select
                    value={studentForm.birthOrder}
                    onChange={(e) => setStudentForm({ ...studentForm, birthOrder: e.target.value })}
                  >
                    <option value="">Select</option>
                    <option value="1st">1st</option>
                    <option value="2nd">2nd</option>
                    <option value="3rd">3rd</option>
                    <option value="4th">4th</option>
                    <option value="5th">5th</option>
                    <option value="6th">6th</option>
                    <option value="7th">7th</option>
                    <option value="8th">8th</option>
                    <option value="9th">9th</option>
                    <option value="10th">10th</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Who Supports Your Studies?</label>
                  <input
                    type="text"
                    value={studentForm.studySupportPerson}
                    onChange={(e) => setStudentForm({ ...studentForm, studySupportPerson: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Relationship</label>
                  <input
                    type="text"
                    value={studentForm.studySupportRelationship}
                    onChange={(e) => setStudentForm({ ...studentForm, studySupportRelationship: e.target.value })}
                    placeholder="e.g. Parent, Guardian, Sibling"
                  />
                </div>
                <div className="form-group">
                  <label>Support Contact No.</label>
                  <input
                    type="text"
                    value={studentForm.studySupportContact}
                    onChange={(e) => setStudentForm({ ...studentForm, studySupportContact: e.target.value })}
                    placeholder="09XX-XXX-XXXX"
                  />
                </div>
                <div className="form-group">
                  <label>Family Type</label>
                  <select
                    value={studentForm.parentType}
                    onChange={(e) => setStudentForm({ ...studentForm, parentType: e.target.value })}
                  >
                    <option value="">Select</option>
                    <option value="Both Parents">Both Parents</option>
                    <option value="Single Parent">Single Parent</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Monthly Family Income</label>
                  <select
                    value={studentForm.monthlyFamilyIncome}
                    onChange={(e) => setStudentForm({ ...studentForm, monthlyFamilyIncome: e.target.value })}
                  >
                    <option value="">Select Income Range</option>
                    <option value="Php 5,000 - below">Php 5,000 - below</option>
                    <option value="Php 5,001 - 10,000">Php 5,001 - 10,000</option>
                    <option value="Php 10,001 - 15,000">Php 10,001 - 15,000</option>
                    <option value="Php 15,001 - 20,000">Php 15,001 - 20,000</option>
                    <option value="Php 20,001 - 25,000">Php 20,001 - 25,000</option>
                    <option value="Php 25,001 - 30,000">Php 25,001 - 30,000</option>
                    <option value="Php 30,001 - 35,000">Php 30,001 - 35,000</option>
                    <option value="Php 35,001 - 40,000">Php 35,001 - 40,000</option>
                    <option value="Php 40,001 - Above">Php 40,001 - Above</option>
                  </select>
                </div>

                {/* Educational Background */}
                <div className="form-section-divider">
                  <span>Educational Background</span>
                </div>

                <div className="form-section-subtitle">
                  <span>Tertiary</span>
                </div>
                <div className="form-group">
                  <label>School Name</label>
                  <input
                    type="text"
                    value={studentForm.tertiarySchool}
                    onChange={(e) => setStudentForm({ ...studentForm, tertiarySchool: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={studentForm.tertiaryAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, tertiaryAddress: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date Graduated</label>
                  <input
                    type="text"
                    value={studentForm.tertiaryDateGraduated}
                    onChange={(e) => setStudentForm({ ...studentForm, tertiaryDateGraduated: e.target.value })}
                    placeholder="Year or N/A"
                  />
                </div>
                <div className="form-group">
                  <label>Course</label>
                  <input
                    type="text"
                    value={studentForm.tertiaryCourse}
                    onChange={(e) => setStudentForm({ ...studentForm, tertiaryCourse: e.target.value })}
                  />
                </div>

                <div className="form-section-subtitle">
                  <span>Senior High School</span>
                </div>
                <div className="form-group">
                  <label>School Name</label>
                  <input
                    type="text"
                    value={studentForm.seniorHighSchool}
                    onChange={(e) => setStudentForm({ ...studentForm, seniorHighSchool: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={studentForm.seniorHighAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, seniorHighAddress: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date Graduated</label>
                  <input
                    type="text"
                    value={studentForm.seniorHighDateGraduated}
                    onChange={(e) => setStudentForm({ ...studentForm, seniorHighDateGraduated: e.target.value })}
                    placeholder="Year"
                  />
                </div>
                <div className="form-group">
                  <label>Course / Strand</label>
                  <input
                    type="text"
                    value={studentForm.seniorHighCourse}
                    onChange={(e) => setStudentForm({ ...studentForm, seniorHighCourse: e.target.value })}
                  />
                </div>

                <div className="form-section-subtitle">
                  <span>Junior High School</span>
                </div>
                <div className="form-group">
                  <label>School Name</label>
                  <input
                    type="text"
                    value={studentForm.juniorHighSchool}
                    onChange={(e) => setStudentForm({ ...studentForm, juniorHighSchool: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={studentForm.juniorHighAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, juniorHighAddress: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date Graduated</label>
                  <input
                    type="text"
                    value={studentForm.juniorHighDateGraduated}
                    onChange={(e) => setStudentForm({ ...studentForm, juniorHighDateGraduated: e.target.value })}
                    placeholder="Year"
                  />
                </div>

                <div className="form-section-subtitle">
                  <span>Elementary</span>
                </div>
                <div className="form-group">
                  <label>School Name</label>
                  <input
                    type="text"
                    value={studentForm.elementarySchool}
                    onChange={(e) => setStudentForm({ ...studentForm, elementarySchool: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={studentForm.elementaryAddress}
                    onChange={(e) => setStudentForm({ ...studentForm, elementaryAddress: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Date Graduated</label>
                  <input
                    type="text"
                    value={studentForm.elementaryDateGraduated}
                    onChange={(e) => setStudentForm({ ...studentForm, elementaryDateGraduated: e.target.value })}
                    placeholder="Year"
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowStudentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingStudent ? 'Update Student' : 'Create Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Teacher Modal */}
      {showTeacherModal && (
        <div className="modal-overlay" onClick={() => setShowTeacherModal(false)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeacher ? 'Update Faculty' : 'Add New Faculty'}</h2>
              <button className="close-btn" onClick={() => setShowTeacherModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}
            
            <form onSubmit={handleSaveTeacher} className="modal-form">
              {/* Profile Image Upload Section */}
              <div className="profile-image-section">
                <div className="profile-image-preview">
                  {teacherProfileImagePreview ? (
                    <img src={teacherProfileImagePreview} alt="Profile preview" />
                  ) : (
                    <div className="no-image">
                      <DefaultAvatar 
                        firstName={teacherForm.firstName} 
                        lastName={teacherForm.lastName} 
                        size="xxl"
                      />
                    </div>
                  )}
                </div>
                <div className="profile-image-actions">
                  <input
                    type="file"
                    ref={teacherFileInputRef}
                    onChange={handleTeacherImageSelect}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => teacherFileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    <FiCamera /> {teacherProfileImagePreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {teacherProfileImagePreview && (
                    <button
                      type="button"
                      className="btn-remove-image"
                      onClick={handleRemoveTeacherImage}
                      disabled={uploadingImage}
                    >
                      <FiX /> Remove
                    </button>
                  )}
                  <span className="image-hint">Max 5MB. JPEG, PNG, GIF, WebP</span>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Employee ID *</label>
                  <input
                    type="text"
                    value={teacherForm.employeeId}
                    onChange={(e) => setTeacherForm({ ...teacherForm, employeeId: e.target.value })}
                    placeholder="CCS-FAC-001"
                    required
                    disabled={editingTeacher}
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })}
                    placeholder="teacher@oc.edu.ph"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={teacherForm.firstName}
                    onChange={(e) => setTeacherForm({ ...teacherForm, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={teacherForm.lastName}
                    onChange={(e) => setTeacherForm({ ...teacherForm, lastName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    value={teacherForm.position}
                    onChange={(e) => setTeacherForm({ ...teacherForm, position: e.target.value })}
                    placeholder="Professor, Instructor, etc."
                  />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={teacherForm.role}
                    onChange={(e) => setTeacherForm({ ...teacherForm, role: e.target.value })}
                    required
                  >
                    <option value="teacher">Lecturer</option>
                    <option value="department_head">Department Head</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {!editingTeacher && (
                  <div className="form-group full-width">
                    <label>Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showTeacherPassword ? "text" : "password"}
                        value={teacherForm.password}
                        onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })}
                        placeholder="Default: teacher123"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowTeacherPassword(!showTeacherPassword)}
                        title={showTeacherPassword ? "Hide password" : "Show password"}
                      >
                        {showTeacherPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowTeacherModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTeacher ? 'Update Faculty' : 'Create Faculty'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordTeacher && (
        <div className="modal-overlay" onClick={() => setShowResetPasswordModal(false)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="close-btn" onClick={() => setShowResetPasswordModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}

            <div className="reset-password-content">
              <p className="reset-password-info">
                Reset password for: <strong>{resetPasswordTeacher.firstName} {resetPasswordTeacher.lastName}</strong>
                <br />
                <span className="reset-password-email">({resetPasswordTeacher.email})</span>
              </p>
              
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min. 6 characters)"
                  minLength={6}
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowResetPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleResetPassword}
                >
                  <FiKey /> Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Own Password Modal */}
      {showChangePasswordModal && (
        <div className="modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Your Password</h2>
              <button className="close-btn" onClick={() => setShowChangePasswordModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}

            <div className="reset-password-content">
              <div className="form-group">
                <label>Current Password *</label>
                <input
                  type="password"
                  value={changePasswordForm.currentPassword}
                  onChange={(e) => setChangePasswordForm({ ...changePasswordForm, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                  required
                />
              </div>

              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={changePasswordForm.newPassword}
                  onChange={(e) => setChangePasswordForm({ ...changePasswordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min. 6 characters)"
                  minLength={6}
                  required
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password *</label>
                <input
                  type="password"
                  value={changePasswordForm.confirmPassword}
                  onChange={(e) => setChangePasswordForm({ ...changePasswordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  minLength={6}
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowChangePasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleChangeOwnPassword}
                >
                  <FiLock /> Change Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreviewModal.show && (
        <div className="image-preview-modal" onClick={() => setImagePreviewModal({ show: false, src: '', alt: '' })}>
          <div className="image-preview-content">
            <button className="close-preview" onClick={() => setImagePreviewModal({ show: false, src: '', alt: '' })}>
              <FiX />
            </button>
            <img src={imagePreviewModal.src} alt={imagePreviewModal.alt} />
            <p className="image-preview-name">{imagePreviewModal.alt}</p>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {qrCodeStudent && (
        <StudentQRCode 
          student={qrCodeStudent} 
          onClose={() => setQrCodeStudent(null)} 
        />
      )}

      {/* Grade Modal */}
      {showGradeModal && (
        <div className="modal-overlay" onClick={() => setShowGradeModal(false)}>
          <div className="modal grade-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGrade ? 'Edit Grade' : 'Add Grade'}</h2>
              <button className="close-btn" onClick={() => setShowGradeModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}
            
            <form onSubmit={handleSubmitGrade} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Course Code *</label>
                  <select
                    name="courseCode"
                    value={gradeForm.courseCode}
                    onChange={handleGradeFormChange}
                    required
                    disabled={editingGrade} // Disable when editing existing grade
                  >
                    <option value="">Select a course code...</option>
                    {/* Get unique course codes for the selected year level and semester */}
                    {[...new Map(
                      subjects
                        .filter(subject => 
                          subject.yearLevel === gradeForm.yearLevel && 
                          subject.semester === gradeForm.semester
                        )
                        .map(subject => [subject.courseCode, subject])
                    ).values()].map(subject => {
                      const schoolYear = getSchoolYearFromYearLevel(gradeForm.yearLevel);
                      const fullyGraded = isSubjectFullyGraded(
                        subject.courseCode, 
                        gradeForm.semester, 
                        gradeForm.yearLevel, 
                        schoolYear
                      );
                      // Check if this specific period is already graded
                      const periodGraded = isSubjectGradedForPeriod(
                        subject.courseCode,
                        gradeForm.semester,
                        gradeForm.yearLevel,
                        schoolYear,
                        gradeForm.period
                      );
                      const isDisabled = (fullyGraded || periodGraded) && !editingGrade;
                      return (
                        <option 
                          key={subject.courseCode} 
                          value={subject.courseCode}
                          disabled={isDisabled}
                          style={isDisabled ? { color: '#9ca3af' } : {}}
                        >
                          {subject.courseCode}
                          {fullyGraded ? ' ✓ (All Complete)' : periodGraded ? ` ✓ (${gradeForm.period} graded)` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {!editingGrade && (
                    <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Subjects marked with ✓ are already graded for the selected period
                    </small>
                  )}
                </div>
                
                {/* Warning when student is NOT enrolled */}
                {gradeForm.courseCode && !studentEnrolled && !editingGrade && (
                  <div className="enrollment-warning" style={{ 
                    background: '#fef2f2', 
                    border: '1px solid #fecaca', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    marginBottom: '1rem',
                    color: '#991b1b'
                  }}>
                    <FiAlertCircle style={{ marginRight: '8px' }} />
                    <strong>Student Not Enrolled!</strong>
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem' }}>
                      This student is not enrolled in {gradeForm.courseCode}. 
                      Please enroll the student first in the Enrollments tab before adding grades.
                    </p>
                  </div>
                )}
                
                {/* Section dropdown - only show if course code is selected and student is enrolled */}
                {gradeForm.courseCode && availableSections.length > 1 && studentEnrolled && (
                  <div className="form-group">
                    <label>
                      Section * 
                      {sectionAutoFilled && gradeForm.section && (
                        <span className="enrollment-indicator" style={{ color: '#22c55e', marginLeft: '8px' }}>
                          ✓ Auto-filled from enrollment
                        </span>
                      )}
                    </label>
                    <select
                      name="section"
                      value={gradeForm.section}
                      onChange={handleGradeFormChange}
                      required
                      style={sectionAutoFilled ? { borderColor: '#22c55e', backgroundColor: '#f0fdf4' } : {}}
                    >
                      <option value="">Select section...</option>
                      {availableSections.map(subject => {
                        const sectionValue = subject.section || 'N/A';
                        // Check if this section is already graded for the current student
                        const isAlreadyGraded = !editingGrade && grades.some(
                          g => g.studentId === gradeForm.studentId &&
                               g.courseCode === subject.courseCode && 
                               g.section === sectionValue &&
                               g.semester === gradeForm.semester && 
                               g.schoolYear === gradeForm.schoolYear &&
                               g.yearLevel === gradeForm.yearLevel &&
                               g.period === gradeForm.period
                        );
                        const instructorName = subject.instructorFirstName && subject.instructorLastName 
                          ? `${subject.instructorFirstName} ${subject.instructorLastName}` 
                          : 'No Instructor';
                        return (
                          <option 
                            key={subject.id} 
                            value={sectionValue}
                            disabled={isAlreadyGraded}
                          >
                            Section {sectionValue} - {instructorName} {isAlreadyGraded ? '(Already Graded)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                
                {/* Show section as read-only if enrolled (section auto-filled) */}
                {gradeForm.courseCode && availableSections.length === 1 && gradeForm.section && studentEnrolled && (
                  <div className="form-group">
                    <label>
                      Section 
                      <span className="enrollment-indicator" style={{ color: '#22c55e', marginLeft: '8px' }}>
                        ✓ Enrolled Section
                      </span>
                    </label>
                    <input
                      type="text"
                      value={gradeForm.section}
                      readOnly
                      className="readonly-input"
                      style={{ borderColor: '#22c55e', backgroundColor: '#f0fdf4' }}
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <label>Subject Title</label>
                  <input
                    type="text"
                    name="subjectTitle"
                    value={gradeForm.subjectTitle}
                    onChange={handleGradeFormChange}
                    placeholder="Auto-filled from subject"
                    readOnly
                  />
                </div>
                
                <div className="form-group">
                  <label>Units</label>
                  <input
                    type="number"
                    name="units"
                    value={gradeForm.units}
                    onChange={handleGradeFormChange}
                    step="0.5"
                    min="0"
                    max="6"
                    readOnly
                  />
                </div>
                
                <div className="form-group">
                  <label>Grade</label>
                  <select
                    name="grade"
                    value={gradeForm.grade}
                    onChange={handleGradeFormChange}
                  >
                    <option value="">Not Graded</option>
                    <option value="1.00">1.00 = 99 - 100 (Excellent)</option>
                    <option value="1.25">1.25 = 96 - 98 (Excellent)</option>
                    <option value="1.50">1.50 = 93 - 95 (Excellent)</option>
                    <option value="1.75">1.75 = 90 - 92 (Very Good)</option>
                    <option value="2.00">2.00 = 87 - 89 (Very Good)</option>
                    <option value="2.25">2.25 = 84 - 86 (Good)</option>
                    <option value="2.50">2.50 = 81 - 83 (Good)</option>
                    <option value="2.75">2.75 = 78 - 80 (Fair)</option>
                    <option value="3.00">3.00 = 75 - 77 (Pass)</option>
                    <option value="4.00">4.00 = Incomplete</option>
                    <option value="5.00">5.00 = Failed</option>
                    <option value="DRP">DRP. = Dropped</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Semester</label>
                  <select
                    name="semester"
                    value={gradeForm.semester}
                    onChange={handleGradeFormChange}
                    required
                    disabled={!editingGrade}
                  >
                    <option value={1}>1st Semester</option>
                    <option value={2}>2nd Semester</option>
                    {gradeForm.yearLevel === 4 && <option value={3}>Summer (OJT)</option>}
                  </select>
                  {!editingGrade && (
                    <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Semester is locked to the current filter. Change the filter to grade a different semester.
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Year Level *</label>
                  <select
                    name="yearLevel"
                    value={gradeForm.yearLevel}
                    onChange={handleGradeFormChange}
                    required
                    disabled={!editingGrade && selectedYearLevel !== null}
                  >
                    <option value={1}>1st Year</option>
                    <option value={2}>2nd Year</option>
                    <option value={3}>3rd Year</option>
                    <option value={4}>4th Year</option>
                  </select>
                  {!editingGrade && selectedYearLevel !== null && (
                    <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Year level is locked to the current filter. Change the filter to grade a different year level.
                    </small>
                  )}
                </div>
                
                {/* Hide grading period for Summer/OJT - only has one grade */}
                {gradeForm.semester !== 3 && (
                  <div className="form-group">
                    <label>Grading Period *</label>
                    <select
                      name="period"
                      value={gradeForm.period}
                      onChange={handleGradeFormChange}
                      required
                    >
                      <option value="prelim">Prelim</option>
                      <option value="midterm">Midterm</option>
                      <option value="prefinal">Pre-Final</option>
                      <option value="final">Final</option>
                    </select>
                  </div>
                )}
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowGradeModal(false)}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!editingGrade && gradeForm.courseCode && !studentEnrolled}
                  title={!editingGrade && gradeForm.courseCode && !studentEnrolled ? 'Student must be enrolled first' : ''}
                >
                  <FiSave /> {editingGrade ? 'Update Grade' : 'Save Grade'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subject Modal */}
      {showSubjectModal && (
        <div className="modal-overlay" onClick={() => setShowSubjectModal(false)}>
          <div className="modal subject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubject ? 'Edit Subject' : 'Add Subject'}</h2>
              <button className="close-btn" onClick={() => setShowSubjectModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}
            
            <form onSubmit={handleSubmitSubject} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Course Code *</label>
                  <input
                    type="text"
                    name="courseCode"
                    value={subjectForm.courseCode}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., CS 401"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Units *</label>
                  <input
                    type="number"
                    name="units"
                    value={subjectForm.units}
                    onChange={handleSubjectFormChange}
                    step="0.5"
                    min="0"
                    max="6"
                    required
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Subject Title *</label>
                  <input
                    type="text"
                    name="subjectTitle"
                    value={subjectForm.subjectTitle}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., Software Engineering"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Semester *</label>
                  <select
                    name="semester"
                    value={subjectForm.semester}
                    onChange={handleSubjectFormChange}
                    required
                  >
                    <option value={1}>1st Semester</option>
                    <option value={2}>2nd Semester</option>
                    {parseInt(subjectForm.yearLevel) === 4 && <option value={3}>Summer (OJT)</option>}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Year Level *</label>
                  <select
                    name="yearLevel"
                    value={subjectForm.yearLevel}
                    onChange={handleSubjectFormChange}
                    required
                  >
                    <option value={1}>1st Year</option>
                    <option value={2}>2nd Year</option>
                    <option value={3}>3rd Year</option>
                    <option value={4}>4th Year</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Section</label>
                  <input
                    type="text"
                    name="section"
                    value={subjectForm.section}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., A, B, 1, 2"
                  />
                </div>
                
                <div className="form-group">
                  <label>Instructor First Name *</label>
                  <input
                    type="text"
                    name="instructorFirstName"
                    value={subjectForm.instructorFirstName}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., John"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Instructor Last Name *</label>
                  <input
                    type="text"
                    name="instructorLastName"
                    value={subjectForm.instructorLastName}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., Doe"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Schedule Days *</label>
                  <select
                    name="scheduleDay"
                    value={subjectForm.scheduleDay}
                    onChange={handleSubjectFormChange}
                    required
                  >
                    <option value="">Select days...</option>
                    <option value="DAILY">Daily</option>
                    <option value="MWF">Monday, Wednesday, Friday (MWF)</option>
                    <option value="WF">Wednesday, Friday (WF)</option>
                    <option value="TTH">Tuesday, Thursday (TTH)</option>
                    <option value="TTHS">Tuesday, Thursday, Saturday (TTHS)</option>
                    <option value="MW">Monday, Wednesday (MW)</option>
                    <option value="TH">Thursday (TH)</option>
                    <option value="SAT">Saturday (SAT)</option>
                    <option value="SUN">Sunday (SUN)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Start Time *</label>
                  <input
                    type="time"
                    name="scheduleTimeStart"
                    value={subjectForm.scheduleTimeStart}
                    onChange={handleSubjectFormChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>End Time *</label>
                  <input
                    type="time"
                    name="scheduleTimeEnd"
                    value={subjectForm.scheduleTimeEnd}
                    onChange={handleSubjectFormChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Room *</label>
                  <input
                    type="text"
                    name="room"
                    value={subjectForm.room}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., Room 301, Lab 1"
                    required
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Pre-requisite</label>
                  <input
                    type="text"
                    name="preRequisite"
                    value={subjectForm.preRequisite}
                    onChange={handleSubjectFormChange}
                    placeholder="e.g., CS 301, CS 302 (leave blank if none)"
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowSubjectModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <FiSave /> {editingSubject ? 'Update Subject' : 'Save Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollmentModal && selectedSubjectForEnrollment && (
        <div className="modal-overlay" onClick={() => setShowEnrollmentModal(false)}>
          <div className="modal enrollment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Enroll Students</h2>
              <button className="close-btn" onClick={() => setShowEnrollmentModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}
            
            <div className="enrollment-info">
              <div className="info-row">
                <strong>Subject:</strong> {selectedSubjectForEnrollment.courseCode} - {selectedSubjectForEnrollment.subjectTitle}
              </div>
              <div className="info-row">
                <strong>Section:</strong> <span className="section-badge">{selectedSubjectForEnrollment.section}</span>
              </div>
              <div className="info-row">
                <strong>Instructor:</strong> {selectedSubjectForEnrollment.instructorFirstName} {selectedSubjectForEnrollment.instructorLastName}
              </div>
              <div className="info-row">
                <strong>Year Level:</strong> {selectedSubjectForEnrollment.yearLevel === 1 ? '1st' : selectedSubjectForEnrollment.yearLevel === 2 ? '2nd' : selectedSubjectForEnrollment.yearLevel === 3 ? '3rd' : '4th'} Year
                <strong style={{ marginLeft: '1rem' }}>Semester:</strong> {selectedSubjectForEnrollment.semester === 1 ? '1st' : selectedSubjectForEnrollment.semester === 2 ? '2nd' : 'Summer'}
              </div>
            </div>
            
            <div className="enrollment-students-list">
              <div className="list-header">
                <h3>Select Students to Enroll</h3>
                <button 
                  type="button" 
                  className="btn-secondary btn-sm"
                  onClick={handleSelectAllStudentsForEnrollment}
                >
                  {selectedStudentsForEnrollment.length === students.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="students-checkbox-list">
                {students.map(student => {
                    const isSelected = selectedStudentsForEnrollment.some(s => s.studentId === student.studentId);
                    // Check if student is already enrolled in ANY section of this subject (same course code, semester, year level)
                    const existingEnrollment = enrollments.find(e => 
                      e.studentId === student.studentId &&
                      e.courseCode === selectedSubjectForEnrollment.courseCode &&
                      e.semester === selectedSubjectForEnrollment.semester &&
                      e.yearLevel === selectedSubjectForEnrollment.yearLevel
                    );
                    const isAlreadyEnrolled = !!existingEnrollment;
                    
                    return (
                      <label 
                        key={student.studentId} 
                        className={`student-checkbox-item ${isSelected ? 'selected' : ''} ${isAlreadyEnrolled ? 'enrolled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleStudentForEnrollment(student)}
                          disabled={isAlreadyEnrolled}
                        />
                        <span className="student-info">
                          <span className="student-id">{student.studentId}</span>
                          <span className="student-name">{student.firstName} {student.lastName}{student.suffix ? ` ${student.suffix}` : ''}</span>
                          <span className="student-section">
                            {student.yearLevel === 1 ? '1st' : student.yearLevel === 2 ? '2nd' : student.yearLevel === 3 ? '3rd' : '4th'} Year {student.section}
                          </span>
                          {isAlreadyEnrolled && (
                            <span className="enrolled-badge">
                              Enrolled in Section {existingEnrollment.section}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                {students.length === 0 && (
                  <div className="empty-state-small">
                    <p>No students found. Please add students first.</p>
                  </div>
                )}
              </div>
              
              <div className="selected-count">
                {selectedStudentsForEnrollment.length} student(s) selected
              </div>
            </div>
            
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowEnrollmentModal(false)}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-primary"
                onClick={handleSubmitEnrollments}
                disabled={selectedStudentsForEnrollment.length === 0}
              >
                <FiUserCheck /> Enroll Selected Students
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Enrollment Modal */}
      {showEditEnrollmentModal && editingEnrollment && (
        <div className="modal-overlay" onClick={() => setShowEditEnrollmentModal(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Class Number</h2>
              <button className="close-btn" onClick={() => setShowEditEnrollmentModal(false)}>
                <FiX />
              </button>
            </div>
            
            {modalError && (
              <div className="modal-error">
                <FiAlertCircle /> {modalError}
              </div>
            )}
            
            <div className="enrollment-info" style={{ marginBottom: '1rem' }}>
              <div className="info-row">
                <strong>Student:</strong> {editingEnrollment.studentId}
              </div>
              <div className="info-row">
                <strong>Subject:</strong> {editingEnrollment.courseCode} - {editingEnrollment.subjectTitle}
              </div>
              <div className="info-row">
                <strong>Section:</strong> <span className="section-badge">{editingEnrollment.section}</span>
              </div>
            </div>
            
            <form onSubmit={handleSaveEnrollment} className="modal-form">
              <div className="form-group">
                <label>Class Number</label>
                <input
                  type="number"
                  value={editEnrollmentForm.classNumber}
                  onChange={(e) => setEditEnrollmentForm({ classNumber: e.target.value })}
                  placeholder="Enter class number"
                  min="1"
                  required
                />
                <small className="form-hint">The student's class number for this section</small>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditEnrollmentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <FiSave /> Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
